import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import '@shoelace-style/shoelace/dist/components/checkbox/checkbox.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/radio/radio.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { WithRealtimeConsumer } from '../mixins/with-realtime-consumer';
import '../components/shop-day-bar';
import '../components/till-staff-bar';
import type { IServingStaff } from '../components/till-staff-bar';
import { clearServingStaff } from '../components/till-staff-bar';
import {
  ApiFactory,
  ISetProductUnavailableRequest,
  ProductAvailabilityReason,
} from '../api/easypos-api';
import { resolveAssetUrl } from '../api/asset-url';
import {
  ICategory,
  IProduct,
  IVariantCell,
  IModifier,
  IModifierGroup,
  ISale,
  ISelectedModifierGroup,
  ISelectedModifier,
  ISaleDiscount,
  IMemberTillContext,
  IMemberPurchaseItem,
  ICreditNote,
  ICreateSaleTender,
} from '../api/model/requests';
import { parseMemberQr } from '../datatypes/member-code';
import { creditNoteRedeemable } from '../datatypes/credit-note';
import { money, activeCurrency } from '../datatypes/currency';
import { newSaleIdempotencyKey } from '../database/sale-outbox';
import { ProductAvailabilityContext } from '../contexts/product-availability-context';
import { ProductAvailabilityStore } from '../state/product-availability-store';
import { resolveRouterPath, router } from '../router';
import { styles as sharedStyles } from '../styles/shared-styles';
import '../components/order-qr';
import '../components/cash-register-dialog';
import '../components/credit-note-picker';
import '../components/split-payment-dialog';

type UntilPreset = 'indefinite' | '1h' | '2h' | '4h' | '8h' | 'custom';

interface CartLine {
  // Stable client-side id so the data-table can key extensions /
  // selections on a value that survives _cart array replacement on
  // each mutation. Not persisted to the server — the server-side sale
  // record assigns its own line ids on submit.
  id: string;
  product: IProduct;
  quantity: number;
  selectedModifierGroups: ISelectedModifierGroup[];
  // Chosen variant cell. Always set — a simple product sells its single
  // default cell. Label derived from the cell's dimension values (null
  // for a simple product's dimensionless cell).
  cellId: string;
  cellLabel: string | null;
  // Computed locally for display:
  unitPrice: number;
  // Optional line-level discount snapshot. Server resolves + clamps; this
  // is what the cashier chose.
  discount?: ISaleDiscount | null;
}

function newCartLineId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `cl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

const Base = WithRealtimeConsumer(
  WithActiveShopConsumer(WithUserProfileConsumer(LitElement))
);

// Cashier register screen.
//
// Two-column layout:
//   left  → menu grid (active items, grouped by category, tap to add)
//   right → current cart (each line shows options, +/-, line subtotal)
// Tapping a recipe item with required modifiers opens an option-picker dialog
// before the line lands in the cart. Tapping a whole item or a recipe with
// no required options adds straight away.
@customElement('sales-new-page')
export class SalesNewPage extends Base {
  @state() private _items: IProduct[] = [];
  @state() private _categories: ICategory[] = [];
  @state() private _cart: CartLine[] = [];
  @state() private _loading = true;
  @state() private _error: string | null = null;
  // Phase D — true when the shop has no OPEN trading day (closed or closing),
  // so the till must be gated. Null until the first load resolves.
  @state() private _dayBlocked: boolean | null = null;
  @state() private _busy = false;
  // Y.C1 — after a successful sale, render a customer-shareable
  // full-screen handoff (large QR + track URL + "Thank you" button)
  // instead of the previous inline "Sale recorded" toast. The cashier
  // turns the device toward the customer to scan/note the URL, then
  // taps Thank you to release back to the empty cart for the next
  // order. _lastSale set ⇒ overlay shown; null ⇒ regular register UI.
  @state() private _lastSale: ISale | null = null;
  // Note: the shop's productDisplayStyle setting used to toggle between
  // image-less rows and image-bearing cards. The cashier-page redesign
  // dropped the rows variant — every tile carries a thumbnail now.
  // Keep the setting on Shop for forward compat; just don't read it
  // here.

  // Prominent menu search. Substring-matches against item name + resolved
  // category name (both lowercased). Empty string = no filter.
  @state() private _search = '';
  // Sticky category-chip filter. null = "All". When set, only items
  // whose categoryId matches are shown. Compatible with _search — both
  // filters apply additively.
  @state() private _activeCategory: string | null = null;
  // Phone / small-tablet viewport switch (≤ 899px). When true, the
  // page shows EITHER the Menu pane or the Order pane (via the
  // top toggle), never both. Desktop / large tablet (≥ 900px) shows
  // both side-by-side as before.
  @state() private _smallViewport = false;
  @state() private _phoneTab: 'menu' | 'order' = 'menu';
  private _smallMq = window.matchMedia('(max-width: 1023px)');
  private _onSmallMq = () => {
    this._smallViewport = this._smallMq.matches;
  };

  // Option-picker dialog state. Each map entry mirrors a SelectedModifierGroup:
  // groupId → list of {modifierId, quantity}. Non-quantifiable picks land
  // with quantity = 1; quantifiable picks carry the per-option qty
  // entered via the in-dialog stepper.
  @state() private _pendingItem: IProduct | null = null;
  @state() private _pendingCellId: string | null = null;
  @state() private _pendingSelections: Record<string, ISelectedModifier[]> = {};
  // Quantity entered in the dialog. Drives both ADD (new line gets
  // this qty) and EDIT (replaces the line's qty). Minimum 1 — the
  // dialog isn't a Remove path; the row trash handles that.
  @state() private _pendingQuantity = 1;
  // D1 — working discount state while the option dialog is open. Saved
  // onto the cart line on confirm. null = no line discount.
  @state() private _pendingDiscount: ISaleDiscount | null = null;
  // Cart-level discount. Applied AFTER per-line discounts at the server.
  @state() private _cartDiscount: ISaleDiscount | null = null;
  @state() private _cartDiscountDialogOpen = false;

  // N.7.4 — who is serving. The staff member who entered their PIN at the till,
  // stamped onto each sale as actingStaffId so the day's takings say who took
  // them. Deliberately NOT cleared after a sale (unlike _member): the customer
  // changes every sale, the person behind the counter does not. null = nobody
  // has identified themselves, which sells perfectly well and records the sale
  // unattributed (@STAFF-SELL-002).
  @state() private _servingStaff: IServingStaff | null = null;

  // Customer-loyalty pivot — member attached to the in-progress cart
  // (scanned wallet QR or entered code). Passed as memberCode on checkout
  // and cleared after the sale. null = anonymous sale.
  @state() private _member: {
    memberCode: string;
    memberName: string;
    loginId: string;
  } | null = null;
  // The attached member's till "welcome" context — store-credit balance + recent
  // purchases (last visit / reorder). Fetched on attach, cleared on remove.
  @state() private _memberContext: IMemberTillContext | null = null;
  @state() private _memberContextLoading = false;
  @state() private _memberDialogOpen = false;
  @state() private _memberCodeInput = '';
  @state() private _memberLookupError: string | null = null;
  @state() private _memberBusy = false;
  // "Same again" reorder picker. Lists the member's last order's lines; each
  // routes through the normal add dialog (size/modifiers confirmed) rather
  // than blindly re-adding (the old _reorderLast dropped variant/modifier
  // choices → invalid line → checkout failed). Stays open under the option
  // dialog so the cashier works through the order one line at a time.
  @state() private _reorderOpen = false;
  // Indices (into the last order's items[]) already added this session, for
  // the picker's ✓ marker. Reassigned (not mutated) for reactivity.
  @state() private _reorderAdded: ReadonlySet<number> = new Set();
  // The reorder line index currently being confirmed in the option dialog,
  // so a successful Add can mark it. null = the open dialog isn't a reorder.
  private _reorderActiveIndex: number | null = null;
  // Working state for the cart-discount dialog (separate from
  // _cartDiscount so Cancel reverts cleanly).
  @state() private _pendingCartDiscount: ISaleDiscount | null = null;
  // When the dialog is opened to EDIT an existing cart line (vs. add a
  // new one), this is the line's id. Confirm replaces that line rather
  // than appending. null = add mode.
  @state() private _editingLineId: string | null = null;

  // Availability dialog state — opened from the ⋮ button on each menu card or
  // by tapping a Sold-out card. Reads the store provided by app-index.
  @consume({ context: ProductAvailabilityContext, subscribe: true })
  @property({ attribute: false })
  private availability?: ProductAvailabilityStore;

  @state() private _availabilityItem: IProduct | null = null;
  @state() private _availReason: ProductAvailabilityReason = 'out-of-stock';
  @state() private _availNote = '';
  @state() private _availUntilPreset: UntilPreset = 'indefinite';
  @state() private _availCustomUntil = '';
  @state() private _availBusy = false;
  @state() private _availError: string | null = null;

  // Tick whenever the availability store fires onChange so render() picks
  // up live flips from other terminals.
  @state() private _availabilityTick = 0;
  private _availabilityUnsub?: () => void;

  // Tracks which shop's cart we've already rehydrated. _load runs on
  // every realtime menuPublished / reconnect event; we must only
  // restore once per (mount, shop), otherwise a publish landing
  // mid-sale would re-merge stale localStorage state on top of the
  // in-progress cart.
  private _cartRestoredForShop: string | null = null;

  // Realtime: refetch the menu when a publish lands or the connection
  // reconnects after a drop. The cashier reads live editorial state
  // (`/menu`), so any menu change committed on another terminal arrives
  // on the next listMenu() — `menuPublished` is the cleanest "rebuild
  // your menu" signal we have today (Phase G's editorialVersionUpdated
  // will give us a finer-grained signal for unpublished edits).
  private _menuPublishedUnsub?: () => void;
  private _reconnectedUnsub?: () => void;

  static styles = [
    sharedStyles,
    css`
      /* Phase D — till gate shown when there is no open trading day. */
      .day-gate {
        max-width: 32rem;
        margin: 2rem auto;
        padding: 2rem 1.5rem;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
      }
      .day-gate sl-icon {
        font-size: 3rem;
        color: var(--sl-color-neutral-400);
      }
      .day-gate shop-day-bar {
        width: 100%;
        margin-top: 0.5rem;
      }
      /* Y.C1 — customer-handoff confirmation overlay. Full-screen
         takeover after a successful sale, intended to be turned
         toward the customer. */
      .confirmation-overlay {
        height: 100%;
        min-height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
        box-sizing: border-box;
        background: var(--app-bg);
      }
      .confirmation-body {
        max-width: 460px;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 14px;
        text-align: center;
      }
      .confirmation-shop {
        font-family: var(--font-mono);
        font-size: 0.78rem;
        font-weight: 600;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--app-text-muted, var(--sl-color-neutral-600));
      }
      .confirmation-order-number {
        font-family: var(--font-mono);
        font-weight: 700;
        font-size: clamp(3rem, 12vw, 4.5rem);
        line-height: 1;
        letter-spacing: -0.04em;
        font-variant-numeric: tabular-nums;
        color: var(--app-text);
      }
      .confirmation-order-number .hash {
        opacity: 0.4;
        margin-right: 2px;
      }
      .confirmation-headline {
        font-family: var(--font-display);
        font-weight: 700;
        font-size: 1.6rem;
        letter-spacing: -0.01em;
      }
      .confirmation-sub {
        color: var(--app-text-muted, var(--sl-color-neutral-600));
        font-size: 0.95rem;
        max-width: 360px;
      }
      .confirmation-url {
        font-family: var(--font-mono);
        font-size: 0.85rem;
        word-break: break-all;
        color: var(--sl-color-primary-700);
        text-decoration: none;
        padding: 8px 14px;
        background: var(--sl-color-neutral-100);
        border-radius: var(--sl-border-radius-medium);
      }
      .sl-theme-dark .confirmation-url {
        background: var(--sl-color-neutral-800);
        color: var(--sl-color-primary-400);
      }
      .confirmation-thank-you {
        margin-top: 8px;
      }
      .confirmation-thank-you::part(base) {
        min-width: 220px;
        font-family: var(--font-display);
        font-weight: 700;
        letter-spacing: 0.02em;
      }

      :host {
        /* Fill the app-shell's <main> so the cashier behaves like an
           "app screen" with its own internal scrollable regions —
           never a long scrolling document. The cart's footer
           (total + take-payment) stays anchored regardless of how
           many lines are in the cart. */
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        max-width: 1200px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      .layout {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 400px;
        gap: 16px;
        align-items: stretch;
        flex: 1 1 auto;
        min-height: 0;
      }
      /* Below 1024 px the cart starts to crowd the menu grid (3 columns
         at 150 px min = 450 px + 16 px gap + 340 px cart = 806 px before
         the menu can even show 3 items). Switch to single-pane phone-
         tab mode while there's still enough room to show the menu
         comfortably. */
      @media (max-width: 1023px) {
        .layout {
          grid-template-columns: 1fr;
        }
      }
      /* Phone-only segmented switcher between Menu and Order. Hidden on
         desktop / large-tablet where both panes are visible side-by-side. */
      .phone-switch {
        display: none;
      }
      @media (max-width: 1023px) {
        .phone-switch {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4px;
          padding: 4px;
          background: var(--sl-color-neutral-100);
          border-radius: var(--sl-border-radius-large);
          margin-bottom: 12px;
          position: sticky;
          top: 0;
          z-index: 6;
        }
        .sl-theme-dark .phone-switch {
          background: var(--sl-color-neutral-800);
        }
        .phone-switch button {
          appearance: none;
          background: transparent;
          color: inherit;
          font: inherit;
          font-weight: 500;
          padding: 10px 12px;
          border: none;
          border-radius: var(--sl-border-radius-medium);
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .phone-switch button[aria-pressed='true'] {
          background: var(--app-surface, white);
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
        }
        .phone-switch .badge {
          background: var(--sl-color-primary-600);
          color: white;
          font-size: 0.7rem;
          font-weight: 700;
          min-width: 18px;
          height: 18px;
          padding: 0 6px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .pane[hidden] {
          display: none;
        }
      }
      /* Sticky bar containing the search input + the category-filter chips.
         Sits at the top of the menu column and stays put while the cashier
         scrolls the grid. The chips are horizontally scrollable when they
         don't fit (rare; most cafes have ≤ 8 categories). */
      /* Sticks to the top of .pane-scroll on every viewport. The phone-
         switch above lives in a different scroll context (it's a child
         of :host, not of .pane-scroll), so there's no overlap to offset
         for here. */
      .menu-stickybar {
        position: sticky;
        top: 0;
        z-index: 5;
        background: var(--app-bg, var(--sl-color-neutral-0));
        padding: 4px 0 8px;
        margin-bottom: 4px;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .menu-stickybar sl-input {
        --sl-input-height-medium: 44px;
        font-size: 1rem;
      }
      .menu-stickybar sl-input::part(base) {
        border-radius: var(--sl-border-radius-large);
      }
      .menu-stickybar sl-input::part(input) {
        font-size: 1rem;
      }

      /* Each pane flexes top-to-bottom inside its grid cell so internal
         sticky-bars + scrollable bodies behave predictably regardless
         of how tall the page-content overall is. */
      .pane {
        display: flex;
        flex-direction: column;
        min-height: 0;
        overflow: hidden;
      }
      .pane-scroll {
        flex: 1 1 auto;
        overflow-y: auto;
        overscroll-behavior: contain;
        padding-right: 4px;
      }
      .menu-pane h2 {
        flex: none;
      }
      .pane-title {
        margin: 0 0 8px 0;
      }
      /* Order pane: receipt fills the remaining height + scrolls
         internally; totals + payment + actions stay anchored. */
      .order-pane h3 {
        flex: none;
      }
      .order-pane .cart-empty {
        flex: 1 1 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0.65;
      }
      /* Member row in the cart (loyalty attach). */
      .member-bar {
        flex: none;
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        box-sizing: border-box;
        /* No outer margin — the menu sticky-bar's flex gap spaces the
           member bar / welcome panel / search input evenly. */
        margin-bottom: 0;
        padding: 8px 10px;
        border-radius: 12px;
        font: inherit;
        text-align: left;
      }
      .member-bar.add {
        appearance: none;
        cursor: pointer;
        color: var(--s-ink, currentColor);
        background: transparent;
        border: 1.5px dashed var(--s-line, var(--sl-color-neutral-300));
      }
      .member-bar.add:hover {
        border-color: var(--sl-color-primary-500);
        background: var(--s-canvas-strong, rgba(15, 30, 55, 0.04));
      }
      .member-bar.add sl-icon {
        font-size: 18px;
        color: var(--s-accent, var(--sl-color-primary-500));
      }
      .member-bar.attached {
        background: var(--s-canvas-strong, rgba(15, 30, 55, 0.05));
        border: 1px solid var(--s-line, var(--sl-color-neutral-200));
      }
      /* Member welcome panel (store credit + last visit / reorder). Sits in
         the menu sticky-bar directly under the member chip; the sticky-bar
         flex gap provides separation, so no outer margin here. */
      .member-context {
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .member-context.loading {
        font-size: 0.85rem;
        color: var(--s-ink-soft, #777);
        padding: 4px 2px;
      }
      .member-credit {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        border-radius: 10px;
        font-size: 0.88rem;
        background: var(--s-success-soft, rgba(0, 160, 80, 0.1));
        color: var(--s-success, rgb(15, 110, 60));
      }
      .member-credit sl-icon {
        font-size: 18px;
        flex: none;
      }
      .member-credit strong {
        font-variant-numeric: tabular-nums;
      }
      .member-last {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 6px 4px 2px;
      }
      .member-last.muted {
        color: var(--s-ink-soft, #777);
        font-size: 0.82rem;
      }
      .member-last__text {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
        line-height: 1.25;
      }
      .member-last__visit {
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--s-ink-soft, #777);
      }
      .member-last__items {
        font-size: 0.86rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .member-reorder {
        flex: none;
        appearance: none;
        border: 1px solid var(--s-line, var(--sl-color-neutral-200));
        background: var(--s-surface, #fff);
        color: var(--s-ink, currentColor);
        font: inherit;
        font-size: 0.82rem;
        font-weight: 600;
        padding: 6px 12px;
        border-radius: 999px;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 5px;
        transition: background 140ms ease;
      }
      .member-reorder:hover {
        background: var(--s-canvas-strong, rgba(15, 30, 55, 0.06));
      }
      .member-reorder sl-icon {
        font-size: 14px;
      }
      .member-bar.attached > sl-icon {
        font-size: 20px;
        color: var(--s-accent, var(--sl-color-primary-500));
        flex: none;
      }
      .member-bar__text {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
        line-height: 1.2;
      }
      .member-bar__text strong {
        font-size: 0.92rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .member-bar__text code {
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: 0.72rem;
        letter-spacing: 0.1em;
        color: var(--s-ink-soft, var(--sl-color-neutral-600));
      }
      .order-pane .order-footer {
        flex: none;
        padding-top: 8px;
        border-top: 1px solid var(--app-border, var(--sl-color-neutral-200));
        background: var(--app-bg, var(--sl-color-neutral-0));
      }
      /* Cart as a printed receipt.
         Each .receipt-line is its own 3-column grid:
           col 1 (flex)  — title / modifier name
           col 2 (auto)  — base unit price / modifier delta
           col 3 (auto)  — line total (qty × line unit price)
         The line total is placed on its own grid row via inline
         style="grid-row: N" where N = modifier count + 2. That
         drops the total below all modifier rows in column 3
         without needing placeholder cells. */
      .cart-receipt {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        overscroll-behavior: contain;
        display: flex;
        flex-direction: column;
        gap: 14px;
        padding: 4px 2px 8px;
        font-variant-numeric: tabular-nums;
      }
      .receipt-line {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto auto;
        column-gap: 14px;
        row-gap: 2px;
        align-items: baseline;
        padding: 6px 6px;
        border-radius: var(--sl-border-radius-medium);
        cursor: pointer;
        user-select: none;
      }
      .receipt-line:hover {
        background: var(--sl-color-neutral-100);
      }
      .sl-theme-dark .receipt-line:hover {
        background: var(--sl-color-neutral-800);
      }
      .receipt-line .rl-title {
        grid-column: 1;
        grid-row: 1;
        font-weight: 600;
        overflow-wrap: anywhere;
      }
      .receipt-line .rl-unit {
        grid-column: 2;
        grid-row: 1;
        text-align: right;
        white-space: nowrap;
      }
      .receipt-line .rl-mod-name {
        grid-column: 1;
        padding-left: 22px;
        position: relative;
        font-size: 0.85rem;
        opacity: 0.85;
        overflow-wrap: anywhere;
      }
      .receipt-line .rl-mod-name::before {
        content: '•';
        position: absolute;
        left: 8px;
        opacity: 0.7;
      }
      .receipt-line .rl-mod-delta {
        grid-column: 2;
        text-align: right;
        font-size: 0.85rem;
        opacity: 0.85;
        white-space: nowrap;
      }
      .receipt-line .rl-line-total {
        grid-column: 3;
        text-align: right;
        font-weight: 700;
        white-space: nowrap;
        padding-left: 10px;
      }
      /* Small-viewport sticky cart summary on the MENU pane — so the
         cashier can see what's in the cart without flipping to Order.
         Mobile-only. */
      .cart-summary-bar {
        display: none;
      }
      @media (max-width: 1023px) {
        .cart-summary-bar {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          margin-top: 8px;
          background: var(--app-surface, white);
          border: 1px solid var(--app-border, var(--sl-color-neutral-200));
          border-radius: var(--sl-border-radius-large);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
          position: sticky;
          bottom: 8px;
          z-index: 4;
        }
        .cart-summary-bar .count {
          font-weight: 600;
          color: var(--sl-color-primary-600);
        }
        .cart-summary-bar .total {
          margin-left: auto;
          font-weight: 700;
          font-variant-numeric: tabular-nums;
        }
      }
      /* Category-filter chips. Horizontally scrollable strip; tap to filter.
         A chip with aria-pressed=true is the active filter. */
      .cat-strip {
        display: flex;
        gap: 6px;
        overflow-x: auto;
        overscroll-behavior-inline: contain;
        scrollbar-width: thin;
        padding-bottom: 2px;
        margin: 0 -4px;
        padding-left: 4px;
        padding-right: 4px;
      }
      .cat-chip {
        appearance: none;
        background: var(--sl-color-neutral-100);
        color: inherit;
        font: inherit;
        font-size: 0.85rem;
        font-weight: 500;
        padding: 6px 12px;
        border: 1px solid transparent;
        border-radius: 999px;
        white-space: nowrap;
        cursor: pointer;
        flex: none;
      }
      .sl-theme-dark .cat-chip {
        background: var(--sl-color-neutral-800);
      }
      .cat-chip:hover {
        background: var(--sl-color-neutral-200);
      }
      .sl-theme-dark .cat-chip:hover {
        background: var(--sl-color-neutral-700);
      }
      .cat-chip[aria-pressed='true'] {
        background: var(--sl-color-primary-600);
        color: white;
        border-color: var(--sl-color-primary-600);
      }
      /* Register catalog — COMPACT ROWS, not image-on-top tiles. The
         cashier is finding an item fast by name, not browsing pictures,
         so each entry is a dense row: a small left thumbnail + name +
         price, with the availability ⋮ on the right. (The image-heavy
         browsing surface is the separate customer Kiosk, built later.)

         auto-fill with a 240px min track means the row count follows the
         menu-pane width with no media queries: 1 column on a phone-width
         pane, 2 on a mid pane, 3 on a wide desktop pane. */
      .menu-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
        gap: 8px;
      }
      /* Menu row — small thumbnail on the left, text on the right. */
      .menu-card {
        appearance: none;
        position: relative;
        text-align: left;
        font: inherit;
        cursor: pointer;
        background: var(--s-surface, var(--sl-color-neutral-0));
        border: 1px solid var(--s-line, var(--sl-color-neutral-200));
        border-radius: 14px;
        padding: 8px;
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 12px;
        box-shadow: var(--app-shadow-sm, 0 1px 2px rgba(15, 30, 55, 0.05));
        transition:
          transform 120ms ease,
          box-shadow 120ms ease,
          border-color 120ms ease;
      }
      .menu-card:hover {
        transform: translateY(-1px);
        box-shadow: var(--app-shadow-md, 0 6px 18px rgba(15, 30, 55, 0.08));
      }
      .menu-card:focus-visible {
        outline: none;
        border-color: var(--s-accent, #f2b441);
        box-shadow: var(--sl-focus-ring, 0 0 0 3px rgba(15, 30, 55, 0.25));
      }
      .menu-card .thumb {
        position: relative;
        width: 56px;
        height: 56px;
        flex: none;
        border-radius: 11px;
        background: var(--s-canvas-strong, var(--sl-color-neutral-100));
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--s-ink-soft, var(--sl-color-neutral-500));
        font-size: 0.6rem;
        text-align: center;
        overflow: hidden;
      }
      .menu-card .thumb img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
      .menu-card .thumb sl-icon {
        font-size: 22px;
        opacity: 0.55;
      }
      .menu-card .meta {
        flex: 1 1 auto;
        min-width: 0;
        padding-right: 30px;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .menu-card .meta strong {
        font-family: var(--font-display, inherit);
        font-size: 0.95rem;
        line-height: 1.25;
        color: var(--s-ink, currentColor);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .menu-card .meta .price {
        font-weight: 700;
        font-size: 0.9rem;
        font-variant-numeric: tabular-nums;
        color: var(--s-ink, currentColor);
      }
      /* Sold-out / unavailable: the row stays (cashier still sees the
         item) but is desaturated, the buy action is suppressed at the
         click handler, and a strip labels the thumbnail. */
      .menu-card.unavailable {
        opacity: 0.62;
      }
      .menu-card.unavailable .thumb {
        filter: grayscale(0.7);
      }
      .sold-out-overlay {
        position: absolute;
        inset: auto 0 0 0;
        text-align: center;
        background: var(--s-danger, #b3261e);
        color: white;
        font-size: 0.5rem;
        font-weight: 700;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        padding: 2px 2px;
        line-height: 1.2;
      }
      /* The ⋮ button is pinned to the right edge of the row.
         Clicks bubble out to a dedicated handler that opens the
         availability dialog without firing the row's add-to-cart click. */
      .menu-card .more-action {
        position: absolute;
        top: 50%;
        right: 2px;
        transform: translateY(-50%);
        z-index: 1;
        color: var(--s-ink-soft, var(--sl-color-neutral-500));
        --sl-input-height-medium: 28px;
      }
      .menu-card .more-action::part(base) {
        padding: 2px;
      }

      .unavailable-tray {
        margin-top: 24px;
      }
      .unavailable-tray .group-title {
        color: var(--sl-color-primary-600);
      }
      .avail-row {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin: 12px 0;
      }
      .avail-row sl-radio-group {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }
      .avail-current {
        margin: 0 0 12px;
      }

      /* Phone-sized: a hair tighter so more rows fit per screenful. */
      @media (max-width: 480px) {
        .menu-card {
          gap: 10px;
          padding: 7px;
        }
        .menu-card .thumb {
          width: 48px;
          height: 48px;
          border-radius: 10px;
        }
        .menu-card .meta strong {
          font-size: 0.88rem;
          line-height: 1.2;
        }
        .menu-card .meta .price {
          font-size: 0.85rem;
        }
      }
      /* The "SELECT SIZE" gate banner — large, high-contrast, impossible to
         miss. Shown only while a required variant/modifier choice is unmet;
         the Add button is disabled in lockstep so the line can't be added
         until it clears. */
      .required-banner {
        background: var(--s-warning, #b87d1d);
        color: #fff;
        font-weight: 800;
        font-size: 1.15rem;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        text-align: center;
        padding: 14px 12px;
        border-radius: var(--sl-border-radius-medium, 6px);
        margin: 0 0 14px 0;
      }
      /* "Same again" reorder picker. */
      .reorder-sub {
        font-size: 0.88rem;
        color: var(--s-ink-soft, #666);
        margin-bottom: 12px;
      }
      .reorder-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .reorder-row {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        text-align: left;
        padding: 12px 14px;
        border: 1px solid var(--s-line, var(--sl-color-neutral-200));
        border-radius: var(--sl-border-radius-medium, 6px);
        background: var(--s-surface, var(--sl-color-neutral-0));
        color: var(--s-ink, inherit);
        font: inherit;
        cursor: pointer;
      }
      .reorder-row:hover:not([disabled]) {
        border-color: var(--sl-color-primary-500);
      }
      .reorder-row[disabled] {
        opacity: 0.55;
        cursor: not-allowed;
      }
      .reorder-row.added {
        border-color: var(--s-success, #1e7f4a);
      }
      .reorder-qty {
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        min-width: 2.2em;
      }
      .reorder-name {
        flex: 1;
        font-weight: 600;
      }
      .reorder-add {
        color: var(--sl-color-primary-600);
        font-size: 1.1rem;
      }
      .reorder-tick {
        color: var(--s-success, #1e7f4a);
        font-size: 1.2rem;
      }
      .reorder-na {
        font-size: 0.78rem;
        color: var(--s-danger, #b3261e);
        font-weight: 600;
      }
      .option-total {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 12px;
        margin: 0 0 12px 0;
        padding-bottom: 10px;
        border-bottom: 1px solid var(--sl-color-neutral-200);
      }
      .option-total-base {
        color: var(--sl-color-neutral-600);
        font-size: 0.9rem;
      }
      .option-total-delta {
        margin-left: 6px;
        opacity: 0.85;
      }
      .option-total-amount {
        font-size: 1.5rem;
        font-weight: 600;
        color: var(--sl-color-primary-600);
      }
      /* Qty stepper inside the option-picker / line-edit dialog.
         Mirrors the stepper that used to live on the cart row;
         living here means both add-mode and edit-mode go through
         the same UI and the cashier can set qty before confirming
         (instead of confirm-then-bump). */
      .dialog-qty-row {
        display: flex;
        align-items: center;
        gap: 12px;
        margin: 8px 0 12px;
        padding: 6px 0;
        border-bottom: 1px solid var(--sl-color-neutral-200);
      }
      .sl-theme-dark .dialog-qty-row {
        border-bottom-color: var(--sl-color-neutral-700);
      }
      .dialog-qty-stepper {
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }
      .dialog-qty-stepper sl-icon-button::part(base) {
        font-size: 1.4rem;
      }
      .dialog-qty-value {
        min-width: 2em;
        text-align: center;
        font-size: 1.25rem;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
      }
      .dialog-qty-each {
        margin-left: auto;
        opacity: 0.65;
      }
      /* Per-option qty stepper inside the modifier-picker. Sits next to
         a Quantifiable option's checkbox so the cashier can ring up
         "Extra shot ×2" without a second dialog. Stepper hidden until
         the option is selected. */
      .opt-qty-row {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 8px;
        border: 1px solid var(--sl-color-neutral-200);
        border-radius: var(--sl-border-radius-medium);
      }
      .sl-theme-dark .opt-qty-row {
        border-color: var(--sl-color-neutral-700);
      }
      .opt-qty-row[data-selected='true'] {
        border-color: var(--sl-color-primary-400);
      }
      .opt-qty-stepper {
        display: inline-flex;
        align-items: center;
        gap: 2px;
      }
      .opt-qty-value {
        min-width: 1.8em;
        text-align: center;
        font-variant-numeric: tabular-nums;
        font-weight: 600;
      }
      .opt-qty-cap {
        margin-left: 2px;
        opacity: 0.55;
      }
      .group-title {
        margin: 12px 0 4px 0;
        opacity: 0.6;
        font-size: 0.8rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
      .total-row {
        display: flex;
        justify-content: space-between;
        font-weight: 700;
        font-size: 1.1rem;
        margin-top: 8px;
      }
      .actions {
        display: flex;
        gap: 8px;
        margin-top: 8px;
      }
      .submit-block sl-select {
        margin-bottom: 8px;
      }
      /* The tender row. Two per line on a phone-width till, so every button stays a
         comfortable target for a thumb rather than shrinking to fit five across. */
      .tender-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
        margin-bottom: 8px;
      }
      .tender-row-secondary {
        grid-template-columns: repeat(3, 1fr);
      }
      .tender::part(base) {
        width: 100%;
      }
      /* Cash and card are nearly every transaction, so they get the height as well as the
         weight. A mis-tap here costs money, and a bigger target is the cheapest defence. */
      .tender-primary::part(base) {
        min-height: 48px;
        font-weight: 600;
      }
      @media (max-width: 420px) {
        .tender-row-secondary {
          grid-template-columns: 1fr 1fr;
        }
      }
    `,
  ];

  @state() private _paymentMethod = 'cash';

  // Cash-register dialog state. Opens when the cashier hits "Take payment"
  // with payment method = cash, so they can compute change without leaving
  // the app. Pure UI aid — the Sale request body shape is unchanged. End-of-
  // day cash drawer reconciliation is Phase J's job (cash counting + petty
  // cash), so we deliberately don't persist amountGiven / change here.
  @state() private _cashDialogOpen = false;

  // P2c - store-credit picker state. A sale may apply ONE credit note (per the
  // multi-tender v1 scope: credit + one cash/card tender). `_appliedCredit`
  // holds the chosen note and the amount of it that will be spent on THIS sale
  // (= min(redeemable, grand total)); the cash/card method settles whatever
  // remains. The picker dialog lists the attached member's notes (loaded into
  // `_memberCreditNotes`) and resolves bearer notes by code.
  @state() private _creditDialogOpen = false;
  @state() private _appliedCredit: {
    note: ICreditNote;
    amount: number;
  } | null = null;
  @state() private _memberCreditNotes: ICreditNote[] = [];
  @state() private _creditNotesLoading = false;

  // P3 - split-payment composer (1 cash + 1 card + N credit). Opt-in via the
  // "Split payment" button; the single-method quick path above is unchanged.
  @state() private _splitDialogOpen = false;

  // A stable client idempotency key for THIS checkout attempt, generated when a
  // credit note is applied. The server requires it for a credit tender and uses
  // it as Sale.Id, so a retry after a lost response re-redeems the SAME note
  // instead of double-spending. Held until the sale succeeds or credit is
  // removed; never regenerated across retries of the same cart.
  private _creditIdempotencyKey: string | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this._smallViewport = this._smallMq.matches;
    this._smallMq.addEventListener('change', this._onSmallMq);
  }

  firstUpdated() {
    void this._load();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeShop') && this.activeShop) {
      void this._load();
      this._wireRealtime();
    }
    if (changed.has('realtime')) this._wireRealtime();
    if (changed.has('availability')) {
      this._availabilityUnsub?.();
      this._availabilityUnsub = this.availability?.onChange(() => {
        this._availabilityTick++;
      });
    }
    // Persist cart on any mutation. Centralising here means every
    // _cart assignment (_addToCart, _changeQty, _removeLine,
    // _clearCart, _submit-on-success) gets persistence for free —
    // no per-call sprinkles to drift out of sync.
    if (changed.has('_cart')) {
      this._persistCart();
      // P2c - a cart change moves the grand total; keep any applied store
      // credit clamped to it (and drop it when the bill can no longer use it).
      this._reconcileCredit();
    }
  }

  // Receipt-style cart rendering. Three implicit columns per line:
  //   col 1 (flex) — item title or modifier bullet name
  //   col 2 (auto, right-aligned) — per-unit price or modifier delta
  //   col 3 (auto, right-aligned) — line total, on its own grid row
  //                                 below all modifier rows
  // Customer-loyalty pivot — the single member component. Feedback #8: it's
  // pinned at the top of the menu sticky-bar, above the search, so the member's
  // identity + store credit + "Same again" stay visible the whole time the
  // cashier builds the order (the cart is a separate tab on phone). One place,
  // one component — not duplicated on the cart pane. A member is attached by
  // scanning the wallet QR (handled by the scan classifier in _onSearchEnter)
  // or by typing the code here.
  private _renderMemberBar() {
    const chip = this._member
      ? html`
          <div class="member-bar attached">
            <sl-icon name="person-badge"></sl-icon>
            <div class="member-bar__text">
              <strong>${this._member.memberName || 'Member'}</strong>
              <code>${this._member.memberCode}</code>
            </div>
            <sl-icon-button
              name="x-lg"
              label="Remove member"
              @click=${this._removeMember}
            ></sl-icon-button>
          </div>
          ${this._renderMemberContext()}
        `
      : html`
          <button class="member-bar add" @click=${this._openMemberDialog}>
            <sl-icon name="person-plus"></sl-icon>
            <span>Add member</span>
          </button>
        `;
    // The add-member dialog is rendered once at the top level (see render()),
    // NOT here — this bar lives inside the Order pane, which is display:none on
    // the phone Menu tab, so a dialog nested here couldn't open from the pinned
    // Menu-pane "Add member" affordance.
    return chip;
  }

  // The member "welcome" panel: store-credit balance + last visit + reorder.
  private _renderMemberContext() {
    if (this._memberContextLoading) {
      return html`<div class="member-context loading">
        Looking up ${this._member?.memberName || 'member'}…
      </div>`;
    }
    const ctx = this._memberContext;
    if (!ctx) return nothing;
    const last = ctx.recentPurchases[0];
    const lastVisit = ctx.lastVisitUtc ? new Date(ctx.lastVisitUtc) : null;
    const usual = last
      ? last.items
          .slice(0, 3)
          .map(
            (i) =>
              `${i.quantity}× ${i.name}${i.variantLabel ? ` (${i.variantLabel})` : ''}`
          )
          .join(', ') + (last.items.length > 3 ? '…' : '')
      : null;
    return html`
      <div class="member-context">
        ${
          ctx.creditBalance > 0
            ? html`<div class="member-credit">
                <sl-icon name="wallet2"></sl-icon>
                <span
                  ><strong>${money(ctx.creditBalance)}</strong> store credit -
                  use it at checkout</span
                >
              </div>`
            : nothing
        }
        ${
          last
            ? html`<div class="member-last">
                <div class="member-last__text">
                  <span class="member-last__visit"
                    >${
                      lastVisit
                        ? `Last visit ${lastVisit.toLocaleDateString()}`
                        : 'Recent order'
                    }</span
                  >
                  <span class="member-last__items">${usual}</span>
                </div>
                <button
                  class="member-reorder"
                  @click=${this._openReorder}
                  title="Re-order from the last order"
                >
                  <sl-icon name="arrow-clockwise"></sl-icon> Same again
                </button>
              </div>`
            : html`<div class="member-last muted">
                No purchases yet — welcome them in.
              </div>`
        }
      </div>
    `;
  }

  private _renderMemberDialog() {
    return html`
      <sl-dialog
        class="full-page-on-phone"
        label="Add member"
        ?open=${this._memberDialogOpen}
        @sl-request-close=${() => {
          this._memberDialogOpen = false;
        }}
      >
        <div
          style="font-size: 0.88rem; color: var(--s-ink-soft, #666); margin-bottom: 10px;"
        >
          Scan the member's wallet QR, or type their member code.
        </div>
        ${
          this._memberLookupError
            ? html`<sl-alert
                variant="warning"
                open
                style="margin-bottom: 10px;"
              >
                ${this._memberLookupError}
              </sl-alert>`
            : nothing
        }
        <sl-input
          label="Member code"
          placeholder="e.g. VTSJH3MW"
          autocapitalize="characters"
          .value=${this._memberCodeInput}
          autofocus
          @sl-input=${(e: Event) => {
            this._memberCodeInput = (e.target as HTMLInputElement).value;
            this._memberLookupError = null;
          }}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Enter') this._submitMemberCode();
          }}
        ></sl-input>
        <sl-button
          slot="footer"
          @click=${() => {
            this._memberDialogOpen = false;
          }}
          >Cancel</sl-button
        >
        <sl-button
          slot="footer"
          variant="primary"
          ?loading=${this._memberBusy}
          ?disabled=${this._memberBusy}
          @click=${this._submitMemberCode}
          >Add</sl-button
        >
      </sl-dialog>
    `;
  }

  private _submitMemberCode() {
    const raw = this._memberCodeInput.trim();
    if (!raw) return;
    // Accept either a bare code or a pasted/scanned wallet QR payload.
    void this._attachMemberByCode(parseMemberQr(raw) ?? raw);
  }

  // Each line is its own grid container so the line total can be
  // pinned to a specific grid-row via inline style without leaking
  // into other lines.
  private _renderCartReceipt() {
    return html`
      <div class="cart-receipt">
        ${this._cart.map((line) => this._renderReceiptLine(line))}
      </div>
    `;
  }

  private _renderReceiptLine(line: CartLine) {
    const groups = line.product.modifierGroups ?? [];
    // Multiply every visible amount (base + modifier deltas) by the
    // line quantity so the column adds up to the line total visibly.
    // Previously each row showed the per-unit amount while the line
    // total showed unit × qty — readers couldn't reconcile the two
    // (e.g. "Latte ×2 base ₱140, Extra shot +₱25, line total ₱370"
    // looked wrong even though it was right). Now everything is
    // in "line quantity" terms.
    const lineQty = Math.max(1, line.quantity);
    interface Mod {
      name: string;
      perUnitQty: number;
      delta: number;
    }
    const mods: Mod[] = [];
    for (const sel of line.selectedModifierGroups) {
      const g = groups.find((gg) => gg.id === sel.groupId);
      if (!g) continue;
      for (const entry of sel.selections) {
        const opt = g.modifiers.find((o) => o.id === entry.modifierId);
        if (!opt) continue;
        // perUnitQty: quantifiable shows "Extra shot ×2" — the per-
        // unit multiplier on the bullet. Non-quantifiable bullets
        // are implicit ×1, no suffix shown.
        const perUnitQty = opt.quantifiable ? Math.max(1, entry.quantity) : 1;
        mods.push({
          name: opt.name,
          perUnitQty,
          // delta is what this modifier contributes to the LINE total —
          // per-unit-delta × per-unit-qty × line-qty.
          delta: opt.priceDelta * perUnitQty * lineQty,
        });
      }
    }
    // Grid rows: 1 = title row, 2..N+1 = modifier rows. Line total
    // shares the LAST content row — title row when there are no
    // modifiers, otherwise the last modifier row. Receipt-style:
    // total reads as the summary of the line it ends, not a separate
    // step below.
    const lineTotalRow = Math.max(1, mods.length + 1);
    // Base must reflect the chosen cell's price, not the product — so a
    // dimensioned line shows the correct base amount. Resolve the cell
    // by id; fall back to the cached unitPrice-derived base if it's gone.
    const cell =
      (line.product.cells ?? []).find((c) => c.id === line.cellId) ?? null;
    const baseLineAmount = (cell?.price ?? line.unitPrice) * lineQty;
    const title = line.cellLabel
      ? `${line.product.name} · ${line.cellLabel}`
      : line.product.name;
    // Line discount preview (server is authoritative; this is a display
    // approximation matching the server's ResolveDiscount logic).
    const lineGross = line.unitPrice * lineQty;
    const lineDiscount = this._previewDiscount(lineGross, line.discount);
    const lineNet = lineGross - lineDiscount;
    return html`
      <div class="receipt-line" @click=${() => this._openLineEdit(line)}>
        <div class="rl-title">${title} ×${line.quantity}</div>
        <div class="rl-unit">${money(baseLineAmount)}</div>
        ${mods.map(
          (m) => html`
            <div class="rl-mod-name">
              ${m.name}${m.perUnitQty > 1 ? ` ×${m.perUnitQty}` : ''}
            </div>
            <div class="rl-mod-delta">
              ${
                m.delta === 0
                  ? ''
                  : `${m.delta > 0 ? '+' : '−'}${money(Math.abs(m.delta))}`
              }
            </div>
          `
        )}
        ${
          lineDiscount > 0
            ? html`
                <div class="rl-mod-name" style="grid-row: ${mods.length + 2};">
                  Discount${line.discount?.kind === 'percent' ? ` (${line.discount.value}%)` : ''}
                </div>
                <div class="rl-mod-delta" style="grid-row: ${mods.length + 2};">
                  −${money(lineDiscount)}
                </div>
              `
            : nothing
        }
        <div
          class="rl-line-total"
          style="grid-row: ${lineDiscount > 0 ? mods.length + 2 : lineTotalRow};"
        >
          ${money(lineNet)}
        </div>
      </div>
    `;
  }

  disconnectedCallback() {
    this._availabilityUnsub?.();
    this._availabilityUnsub = undefined;
    this._menuPublishedUnsub?.();
    this._menuPublishedUnsub = undefined;
    this._reconnectedUnsub?.();
    this._reconnectedUnsub = undefined;
    this._smallMq.removeEventListener('change', this._onSmallMq);
    super.disconnectedCallback();
  }

  private _wireRealtime() {
    this._menuPublishedUnsub?.();
    this._menuPublishedUnsub = undefined;
    this._reconnectedUnsub?.();
    this._reconnectedUnsub = undefined;
    if (!this.realtime) return;
    this._menuPublishedUnsub = this.realtime.on('menuPublished', () => {
      void this._load();
    });
    this._reconnectedUnsub = this.realtime.onReconnected(() => {
      void this._load();
    });
  }

  private async _load() {
    if (!this.activeShop) {
      this._error = 'No active shop.';
      this._loading = false;
      return;
    }
    // Clear any stale error from an earlier _load that ran before the
    // active-shop context hydrated (which would have set "No active
    // shop."). Without this the stale banner persists into the order
    // pane and the option dialog even after the menu loads fine.
    this._error = null;
    this._loading = true;
    try {
      const api = ApiFactory.authenticated();
      const [menu, cats, day] = await Promise.all([
        api.listMenu(this.activeShop.shopName),
        api.listCategories(this.activeShop.shopName),
        api.getCurrentDay(this.activeShop.shopName),
      ]);
      if (menu.data) this._items = menu.data.filter((m) => m.isActive);
      if (cats.data) this._categories = cats.data.filter((c) => c.isActive);
      // Phase D — block ringing unless a trading day is OPEN. Unknown (fetch
      // failed) does not block; the server still rejects a sale with no day.
      if (day.success && day.data) {
        this._setDayBlocked(day.data.state !== 'open');
      }
      // Rehydrate persisted cart against the freshly-loaded menu —
      // but only once per (mount, shop). _load runs on every realtime
      // publish too, and re-restoring then would clobber the in-
      // progress cart with stale storage. Restore joins lines on
      // productId; lines whose item is gone/inactive are silently
      // dropped so a cart for a since-deleted item can't ring up
      // phantom revenue.
      const shop = this.activeShop.shopName;
      if (this._cartRestoredForShop !== shop) {
        this._cartRestoredForShop = shop;
        this._restoreCart();
      }
    } finally {
      this._loading = false;
    }
  }

  // Cart persistence — survives F5 / accidental tab close / PWA
  // update reload. Local-only: localStorage is per browser
  // installation, so no need to mix the deviceId into the key.
  // Per-shop key isolates the cart when a multi-shop owner flips
  // between shops mid-sale.
  //
  // Stored shape is minimal (productId + quantity + selectedModifierGroups);
  // unitPrice is recomputed from the live menu on rehydrate, so a
  // price change between save and restore is picked up automatically.
  private _cartStorageKey(): string | null {
    const shop = this.activeShop?.shopName;
    return shop ? `cashier-cart:${shop.toLowerCase()}` : null;
  }

  private _persistCart() {
    const key = this._cartStorageKey();
    if (!key) return;
    try {
      if (this._cart.length === 0) {
        localStorage.removeItem(key);
        return;
      }
      const payload = {
        lines: this._cart.map((l) => ({
          productId: l.product.id,
          cellId: l.cellId,
          quantity: l.quantity,
          selectedModifierGroups: l.selectedModifierGroups,
          discount: l.discount ?? null,
        })),
        cartDiscount: this._cartDiscount,
      };
      localStorage.setItem(key, JSON.stringify(payload));
    } catch {
      // localStorage can throw (quota, private-window restrictions);
      // best-effort persistence, never surface to the cashier.
    }
  }

  private _restoreCart() {
    const key = this._cartStorageKey();
    if (!key) return;
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(key);
    } catch {
      return;
    }
    if (!raw) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      localStorage.removeItem(key);
      return;
    }
    // New shape: { lines: [...], cartDiscount: ISaleDiscount|null }.
    // An old array-shaped payload from before D1 is dropped (operator
    // starts with an empty cart on next load — ephemeral state).
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      localStorage.removeItem(key);
      return;
    }
    const root = parsed as { lines?: unknown; cartDiscount?: unknown };
    if (!Array.isArray(root.lines)) return;
    // Restore cart discount if present (shape-validated lightly).
    if (root.cartDiscount && typeof root.cartDiscount === 'object') {
      const d = root.cartDiscount as {
        kind?: unknown;
        value?: unknown;
        reason?: unknown;
      };
      if (
        (d.kind === 'percent' || d.kind === 'fixed') &&
        typeof d.value === 'number' &&
        d.value >= 0
      ) {
        this._cartDiscount = {
          kind: d.kind,
          value: d.value,
          reason: typeof d.reason === 'string' ? d.reason : null,
        };
      }
    }
    const rehydrated: CartLine[] = [];
    for (const entry of root.lines) {
      if (!entry || typeof entry !== 'object') continue;
      const e = entry as {
        productId?: unknown;
        cellId?: unknown;
        quantity?: unknown;
        selectedModifierGroups?: unknown;
        discount?: unknown;
      };
      if (typeof e.productId !== 'string') continue;
      const qty = typeof e.quantity === 'number' ? Math.floor(e.quantity) : 0;
      if (qty <= 0) continue;
      const product = this._items.find((m) => m.id === e.productId);
      // Item removed / deactivated since save — silently drop the
      // line. Cashier sees the cart they expect, minus anything no
      // longer sellable.
      if (!product) continue;
      // Defensively coerce + validate the persisted selectedModifierGroups shape.
      // Per the operating-mode rules we don't write migration code, but
      // the persistence layer should silently drop anything that doesn't
      // round-trip cleanly into the current type — including old-shape
      // entries from before the quantifiable-options flip (which used
      // optionIds: string[] instead of selections[]).
      const selectedModifierGroups: ISelectedModifierGroup[] = [];
      if (Array.isArray(e.selectedModifierGroups)) {
        for (const raw of e.selectedModifierGroups) {
          if (!raw || typeof raw !== 'object') continue;
          const so = raw as { groupId?: unknown; selections?: unknown };
          if (typeof so.groupId !== 'string') continue;
          if (!Array.isArray(so.selections)) continue;
          const entries: ISelectedModifier[] = [];
          for (const r of so.selections) {
            if (!r || typeof r !== 'object') continue;
            const en = r as { modifierId?: unknown; quantity?: unknown };
            if (typeof en.modifierId !== 'string') continue;
            const q =
              typeof en.quantity === 'number' ? Math.floor(en.quantity) : 0;
            if (q < 1) continue;
            entries.push({ modifierId: en.modifierId, quantity: q });
          }
          if (entries.length > 0)
            selectedModifierGroups.push({
              groupId: so.groupId,
              selections: entries,
            });
        }
      }
      // Resolve the saved cell against the live product. A simple product
      // (no variant dimensions) always falls back to its single active
      // cell. A dimensioned product whose saved cell no longer resolves
      // is no longer sellable as-is — drop the line (mirrors the old
      // variant-drop logic).
      const activeCells = this._activeCells(product);
      const dimensioned = this._hasVariants(product);
      let cell: IVariantCell | null = null;
      if (typeof e.cellId === 'string') {
        cell = activeCells.find((c) => c.id === e.cellId) ?? null;
      }
      if (!cell && !dimensioned) {
        cell = activeCells[0] ?? null;
      }
      if (!cell) continue;
      const unitPrice = this._computeUnitPrice(
        product,
        cell,
        selectedModifierGroups
      );
      // Restore line discount if present + shape-valid.
      let discount: ISaleDiscount | null = null;
      if (e.discount && typeof e.discount === 'object') {
        const d = e.discount as {
          kind?: unknown;
          value?: unknown;
          reason?: unknown;
        };
        if (
          (d.kind === 'percent' || d.kind === 'fixed') &&
          typeof d.value === 'number' &&
          d.value >= 0
        ) {
          discount = {
            kind: d.kind,
            value: d.value,
            reason: typeof d.reason === 'string' ? d.reason : null,
          };
        }
      }
      rehydrated.push({
        id: newCartLineId(),
        product,
        quantity: qty,
        selectedModifierGroups,
        cellId: cell.id,
        cellLabel: this._cellLabel(product, cell),
        unitPrice,
        discount,
      });
    }
    if (rehydrated.length > 0) this._cart = rehydrated;
  }

  private _categoryName(id: string | null): string {
    if (!id) return 'Other';
    return this._categories.find((c) => c.id === id)?.name ?? id;
  }

  // Whether this product needs an explicit cell choice. A product with
  // one or more variant dimensions has a cell per carried combination;
  // a simple product has exactly one default cell sold directly.
  private _hasVariants(item: IProduct): boolean {
    return (item.variantDimensions?.length ?? 0) > 0;
  }

  // The product's active, sellable cells (isActive defaults to true).
  private _activeCells(item: IProduct): IVariantCell[] {
    return (item.cells ?? []).filter((c) => c.isActive ?? true);
  }

  // The variant picker's heading, named after the product's dimension(s)
  // (e.g. "Size", "Size & Flavor") so the cashier knows WHAT to pick rather
  // than a generic "Variant". Falls back to "Variant" if a dimension has no
  // name. Upper-cased by the caller for the SELECT banner.
  private _variantChoiceLabel(item: IProduct): string {
    const names = (item.variantDimensions ?? [])
      .map((d) => d.name?.trim())
      .filter(Boolean);
    return names.length ? (names as string[]).join(' & ') : 'Variant';
  }

  // The required choices not yet satisfied for the pending item: the variant
  // dimension(s) when no cell is chosen, plus every required modifier group
  // with no selection. Drives the striking "SELECT …" banner and gates the
  // Add button so an invalid line (the root of the broken "Same again") can
  // never be confirmed. Names match what the cashier sees in the dialog.
  private _unmetRequired(): string[] {
    const item = this._pendingItem;
    if (!item) return [];
    const unmet: string[] = [];
    if (this._hasVariants(item) && !this._pendingCellId) {
      unmet.push(this._variantChoiceLabel(item));
    }
    for (const g of item.modifierGroups ?? []) {
      if (!g.required) continue;
      const sel = this._pendingSelections[g.id] ?? [];
      if (sel.length === 0) unmet.push(g.name);
    }
    return unmet;
  }

  // Human-readable label for a cell, derived from its dimension values
  // (the cell carries only value ids). Null for a simple product's
  // dimensionless default cell.
  private _cellLabel(item: IProduct, cell: IVariantCell): string | null {
    if (!cell.dimensionValueIds?.length) return null;
    const parts: string[] = [];
    (item.variantDimensions ?? []).forEach((dim, i) => {
      const vid = cell.dimensionValueIds[i];
      parts.push(dim.values.find((v) => v.id === vid)?.label ?? vid);
    });
    return parts.length ? parts.join(' / ') : null;
  }

  private _computeUnitPrice(
    item: IProduct,
    cell: IVariantCell,
    selected: ISelectedModifierGroup[]
  ): number {
    let price = cell.price;
    for (const sel of selected) {
      const group = item.modifierGroups.find((g) => g.id === sel.groupId);
      if (!group) continue;
      for (const entry of sel.selections) {
        const opt = group.modifiers.find((o) => o.id === entry.modifierId);
        if (!opt) continue;
        // Quantifiable options multiply by the per-selection qty; non-
        // quantifiable always count once (server enforces qty=1, but we
        // belt-and-braces here too so a stale tab can't mis-bill).
        const qty = opt.quantifiable ? Math.max(1, entry.quantity) : 1;
        price += opt.priceDelta * qty;
      }
    }
    return price;
  }

  private _addToCart(
    item: IProduct,
    cell: IVariantCell,
    selected: ISelectedModifierGroup[],
    quantity = 1,
    discount: ISaleDiscount | null = null
  ) {
    const unitPrice = this._computeUnitPrice(item, cell, selected);
    // Merge with same item+options already in cart? Skip for v1; keep separate
    // lines so the cashier can find the line they just added (the new line
    // sits at the bottom of the cart unambiguously). Editing an existing
    // line goes through the dialog and bumps that line's qty in place.
    this._cart = [
      ...this._cart,
      {
        id: newCartLineId(),
        product: item,
        quantity,
        selectedModifierGroups: selected,
        cellId: cell.id,
        cellLabel: this._cellLabel(item, cell),
        unitPrice,
        discount,
      },
    ];
  }

  // Single entry point for adding a product. A plain tap ALWAYS opens the
  // picker dialog — even an option-less product — so a stray tap never
  // silently lands a line (accidental-click protection) and the add flow is
  // uniform: every product → dialog → confirm → cart. The ONLY way to ring
  // straight to the cart with no dialog is a deliberate, cell-resolved scan
  // (a `presetCellId` on a modifier-less product); a typed-code match routes
  // through the same fast path. `presetCellId` is otherwise prefilled into
  // the dialog so the scanned cell starts selected.
  private _beginAdd(item: IProduct, presetCellId: string | null = null) {
    if (this.availability?.isUnavailable(item.id)) {
      this._openAvailabilityDialog(item);
      return;
    }
    const hasVariants = this._hasVariants(item);
    const hasModifiers = (item.modifierGroups?.length ?? 0) > 0;
    const activeCells = this._activeCells(item);

    // Fast path: a scanned/preset cell on a modifier-less product rings up
    // that exact cell with no dialog (a scan is a deliberate action, speed
    // matters). Anything with modifiers still opens the dialog so required
    // choices are made.
    if (presetCellId && !hasModifiers) {
      const c = activeCells.find((x) => x.id === presetCellId);
      if (c) {
        this._addToCart(item, c, [], 1);
        return;
      }
    }

    // Open the dialog. Preselect a cell when there's exactly one sensible
    // choice: a preset (scanned) cell, a simple product's single default
    // cell, or a single-cell dimensioned product. A multi-cell dimensioned
    // product starts with no cell so the cashier must pick (gated below).
    let initialCellId = presetCellId;
    if (!initialCellId) {
      if (!hasVariants) initialCellId = activeCells[0]?.id ?? null;
      else if (activeCells.length === 1) initialCellId = activeCells[0].id;
    }
    this._pendingItem = item;
    this._pendingCellId = initialCellId;
    this._pendingSelections = {};
    this._pendingQuantity = 1;
    this._pendingDiscount = null;
    this._editingLineId = null;
    this._error = null;
  }

  private _onProductClick(item: IProduct) {
    // If the search box holds an exact barcode for THIS product, tapping
    // the filtered tile carries that cell through — same as scanning.
    const q = this._search.trim();
    if (q) {
      const hit = this._resolveCode(q);
      if (hit && hit.product.id === item.id) {
        this._beginAdd(item, hit.cell.id);
        this._search = '';
        return;
      }
    }
    this._beginAdd(item);
  }

  private _openAvailabilityDialog(item: IProduct) {
    this._availabilityItem = item;
    this._availError = null;
    const current = this.availability?.getUnavailable(item.id);
    if (current) {
      // Pre-fill from current state so a manager can review without re-typing.
      this._availReason = current.reason;
      this._availNote = current.reasonNote ?? '';
      if (current.unavailableUntilUtc) {
        // datetime-local expects YYYY-MM-DDTHH:MM in *local* time; the
        // record stores UTC, so format the local components by hand to
        // avoid timezone-shift bugs.
        const dt = new Date(current.unavailableUntilUtc);
        const pad = (n: number) => String(n).padStart(2, '0');
        this._availUntilPreset = 'custom';
        this._availCustomUntil =
          `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}` +
          `T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
      } else {
        this._availUntilPreset = 'indefinite';
        this._availCustomUntil = '';
      }
    } else {
      this._availReason = 'out-of-stock';
      this._availNote = '';
      this._availUntilPreset = 'indefinite';
      this._availCustomUntil = '';
    }
  }

  private _closeAvailabilityDialog() {
    this._availabilityItem = null;
    this._availError = null;
    this._availBusy = false;
  }

  private _resolveUntilUtc(): string | undefined {
    if (this._availUntilPreset === 'indefinite') return undefined;
    if (this._availUntilPreset === 'custom') {
      if (!this._availCustomUntil) return undefined;
      const dt = new Date(this._availCustomUntil);
      if (isNaN(dt.getTime())) return undefined;
      return dt.toISOString();
    }
    const hours = parseInt(this._availUntilPreset, 10);
    return new Date(Date.now() + hours * 3600 * 1000).toISOString();
  }

  private async _confirmMarkUnavailable() {
    if (!this._availabilityItem || !this.activeShop) return;
    if (this._availReason === 'custom' && this._availNote.trim().length === 0) {
      this._availError = 'A note is required when reason is "custom".';
      return;
    }
    if (this._availUntilPreset === 'custom' && !this._availCustomUntil) {
      this._availError = 'Pick a date/time for the custom until.';
      return;
    }
    this._availBusy = true;
    this._availError = null;
    try {
      const body: ISetProductUnavailableRequest = {
        reason: this._availReason,
        reasonNote: this._availNote.trim() || undefined,
        unavailableUntilUtc: this._resolveUntilUtc(),
      };
      const r = await ApiFactory.authenticated().setProductUnavailable(
        this.activeShop.shopName,
        this._availabilityItem.id,
        body
      );
      if (!r.success || !r.data) {
        this._availError = r.error ?? 'Could not mark item unavailable.';
        return;
      }
      // Optimistic local apply so the UI updates instantly even before the
      // SignalR echo. The store's applyEvent is idempotent on the realtime
      // event arriving moments later.
      this.availability?.applyEvent({
        itemId: r.data.itemId,
        isUnavailable: true,
        sinceUtc: r.data.sinceUtc,
        byLoginId: r.data.byLoginId,
        reason: r.data.reason,
        reasonNote: r.data.reasonNote,
        unavailableUntilUtc: r.data.unavailableUntilUtc,
      });
      this._closeAvailabilityDialog();
    } finally {
      this._availBusy = false;
    }
  }

  private async _confirmMarkAvailable() {
    if (!this._availabilityItem || !this.activeShop) return;
    this._availBusy = true;
    this._availError = null;
    try {
      const r = await ApiFactory.authenticated().clearProductUnavailable(
        this.activeShop.shopName,
        this._availabilityItem.id
      );
      if (!r.success) {
        this._availError = r.error ?? 'Could not mark item available.';
        return;
      }
      this.availability?.applyEvent({
        itemId: this._availabilityItem.id,
        isUnavailable: false,
      });
      this._closeAvailabilityDialog();
    } finally {
      this._availBusy = false;
    }
  }

  private _confirmOptions() {
    if (!this._pendingItem) return;
    const selected: ISelectedModifierGroup[] = Object.entries(
      this._pendingSelections
    )
      .filter(([, entries]) => entries.length > 0)
      .map(([groupId, entries]) => ({ groupId, selections: entries }));

    // A dimensioned product requires a cell to be chosen. A simple
    // product falls back to its single active default cell.
    const hasVariants = this._hasVariants(this._pendingItem);
    const activeCells = this._activeCells(this._pendingItem);
    let cell: IVariantCell | null = null;
    if (hasVariants) {
      cell = activeCells.find((c) => c.id === this._pendingCellId) ?? null;
      if (!cell) {
        this._error = 'Please pick a variant.';
        return;
      }
    } else {
      cell = activeCells[0] ?? null;
      if (!cell) {
        this._error = 'This item has no sellable price.';
        return;
      }
    }

    // Validate required groups locally for fast feedback (server re-checks too).
    for (const g of this._pendingItem.modifierGroups) {
      const sel = selected.find((s) => s.groupId === g.id);
      if (g.required && (!sel || sel.selections.length === 0)) {
        this._error = `Please pick a ${g.name}.`;
        return;
      }
    }
    // Clamp qty defensively (stepper handles UI bounds, but a stale
    // _pendingQuantity from a previous dialog could in theory be 0).
    const qty = Math.max(1, Math.floor(this._pendingQuantity || 1));
    this._error = null;
    if (this._editingLineId) {
      // Edit-mode: replace the existing line in place. Updates qty +
      // selectedModifierGroups + cell + unitPrice but preserves the
      // stable id so any per-line UI keeps referring to the same line.
      const id = this._editingLineId;
      const item = this._pendingItem;
      const chosen = cell;
      const newUnitPrice = this._computeUnitPrice(item, chosen, selected);
      this._cart = this._cart.map((l) =>
        l.id === id
          ? {
              ...l,
              product: item,
              selectedModifierGroups: selected,
              cellId: chosen.id,
              cellLabel: this._cellLabel(item, chosen),
              unitPrice: newUnitPrice,
              quantity: qty,
              discount: this._pendingDiscount,
            }
          : l
      );
    } else {
      this._addToCart(
        this._pendingItem,
        cell,
        selected,
        qty,
        this._pendingDiscount
      );
    }
    // If this Add came from the "Same again" picker, mark the line done (✓)
    // so the cashier sees their progress through the order.
    if (this._reorderActiveIndex !== null) {
      this._reorderAdded = new Set([
        ...this._reorderAdded,
        this._reorderActiveIndex,
      ]);
      this._reorderActiveIndex = null;
    }
    this._pendingItem = null;
    this._pendingCellId = null;
    this._pendingSelections = {};
    this._pendingQuantity = 1;
    this._pendingDiscount = null;
    this._editingLineId = null;
  }

  private _cancelOptions() {
    // Backing out of a reorder line leaves it unmarked so it can be retried.
    this._reorderActiveIndex = null;
    this._pendingItem = null;
    this._pendingCellId = null;
    this._pendingSelections = {};
    this._pendingQuantity = 1;
    this._pendingDiscount = null;
    this._editingLineId = null;
  }

  // Open the dialog in EDIT mode for an existing cart line — works
  // for ANY cart line, with or without options. Prefills the qty
  // stepper from line.quantity and any option selections from
  // line.selectedModifierGroups, so the dialog reads as the line's current
  // state.
  private _openLineEdit(line: CartLine) {
    const selections: Record<string, ISelectedModifier[]> = {};
    for (const sel of line.selectedModifierGroups) {
      selections[sel.groupId] = sel.selections.map((e) => ({ ...e }));
    }
    this._pendingItem = line.product;
    this._pendingCellId = line.cellId;
    this._pendingSelections = selections;
    this._pendingQuantity = line.quantity;
    this._pendingDiscount = line.discount ?? null;
    this._editingLineId = line.id;
  }

  private _bumpPendingQuantity(delta: number) {
    const next = Math.max(1, (this._pendingQuantity || 1) + delta);
    this._pendingQuantity = next;
  }

  // Remove the line being edited and close the dialog. Mirrors the
  // old per-row trash icon, just from the dialog footer — the row
  // itself no longer carries a remove control to keep the receipt
  // look clean.
  private _removeLineFromDialog() {
    if (!this._editingLineId) return;
    this._removeLine(this._editingLineId);
    this._cancelOptions();
  }

  // Toggle a non-quantifiable option's inclusion. For single-select
  // groups this replaces the selection; for multi-select it adds or
  // removes. Quantifiable options use _bumpOptionQuantity instead — a
  // toggle becomes "set qty to 1 (on) or 0 (off)".
  private _setOption(group: IModifierGroup, opt: IModifier, checked: boolean) {
    const cur = this._pendingSelections[group.id] ?? [];
    if (!group.multiSelect) {
      this._pendingSelections = {
        ...this._pendingSelections,
        [group.id]: checked ? [{ modifierId: opt.id, quantity: 1 }] : [],
      };
      return;
    }
    if (checked) {
      // Add (qty 1 by default; the per-option stepper bumps after).
      if (cur.some((e) => e.modifierId === opt.id)) return;
      this._pendingSelections = {
        ...this._pendingSelections,
        [group.id]: [...cur, { modifierId: opt.id, quantity: 1 }],
      };
    } else {
      this._pendingSelections = {
        ...this._pendingSelections,
        [group.id]: cur.filter((e) => e.modifierId !== opt.id),
      };
    }
  }

  // Bump or set the qty for a quantifiable option in a multi-select
  // group. delta > 0 adds (and selects the option if not already
  // picked); delta < 0 removes when qty drops to 0. Clamped to
  // [1, opt.maxQuantity] when on, with 0 meaning "deselect."
  private _bumpOptionQuantity(
    group: IModifierGroup,
    opt: IModifier,
    delta: number
  ) {
    const cur = this._pendingSelections[group.id] ?? [];
    const idx = cur.findIndex((e) => e.modifierId === opt.id);
    const currentQty = idx >= 0 ? cur[idx].quantity : 0;
    const max =
      opt.quantifiable && opt.maxQuantity != null
        ? opt.maxQuantity
        : Number.MAX_SAFE_INTEGER;
    const nextQty = Math.max(0, Math.min(max, currentQty + delta));
    if (nextQty === 0) {
      this._pendingSelections = {
        ...this._pendingSelections,
        [group.id]: cur.filter((_, i) => i !== idx),
      };
      return;
    }
    if (idx >= 0) {
      this._pendingSelections = {
        ...this._pendingSelections,
        [group.id]: cur.map((e, i) =>
          i === idx ? { ...e, quantity: nextQty } : e
        ),
      };
    } else {
      this._pendingSelections = {
        ...this._pendingSelections,
        [group.id]: [...cur, { modifierId: opt.id, quantity: nextQty }],
      };
    }
  }

  private _removeLine(lineId: string) {
    this._cart = this._cart.filter((l) => l.id !== lineId);
  }

  private _clearCart() {
    this._cart = [];
    this._cartDiscount = null;
  }

  // Pre-discount subtotal: sum of (unitPrice × quantity) across all cart
  // lines. Server treats this as Subtotal; line/cart discounts are
  // computed and stored separately on the sale.
  private get _subtotal(): number {
    return this._cart.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0);
  }

  // Apply a discount locally for display only — server is authoritative,
  // validates, and clamps. Returns the currency amount (>= 0, <= target).
  private _previewDiscount(
    target: number,
    d: ISaleDiscount | null | undefined
  ): number {
    if (!d || d.value < 0) return 0;
    let amount = 0;
    if (d.kind === 'percent') {
      amount =
        Math.round(((target * Math.min(d.value, 100)) / 100) * 100) / 100;
    } else {
      amount = d.value;
    }
    if (amount > target) amount = target;
    return amount;
  }

  // Sum of per-line discount amounts (preview only).
  private get _lineDiscountTotal(): number {
    return this._cart.reduce(
      (sum, l) =>
        sum + this._previewDiscount(l.unitPrice * l.quantity, l.discount),
      0
    );
  }

  // Cart-level discount amount (preview only) — target is Subtotal minus
  // line-level discounts.
  private get _cartDiscountAmount(): number {
    return this._previewDiscount(
      this._subtotal - this._lineDiscountTotal,
      this._cartDiscount
    );
  }

  // Final total preview (matches server's computation).
  private get _grandTotal(): number {
    return this._subtotal - this._lineDiscountTotal - this._cartDiscountAmount;
  }

  // P2c - store credit applied to this sale (₱), 0 when none.
  private get _appliedCreditAmount(): number {
    return this._appliedCredit?.amount ?? 0;
  }

  // What the cash/card tender must still cover after store credit, clamped at 0.
  private get _amountDue(): number {
    const due =
      Math.round((this._grandTotal - this._appliedCreditAmount) * 100) / 100;
    return due > 0 ? due : 0;
  }

  // Phase J2 — the shop-day-bar fires this on every day/shift change. The till
  // gate is now "is THIS device on an open shift?" (the server rejects a sale
  // without one). Starting a shift from the bar opens the day and reveals the till.
  private _onDayChanged(
    e: CustomEvent<{ state: string; hasOpenShift?: boolean }>
  ) {
    this._setDayBlocked(!e.detail.hasOpenShift);
  }

  // N.7.4 — the one place _dayBlocked is written, so that losing the shift also
  // forgets who is serving. Only the transition INTO blocked clears: _load runs
  // on every realtime publish, and clearing on each evaluation would wipe a name
  // that is still perfectly true.
  private _setDayBlocked(blocked: boolean) {
    if (blocked && this._dayBlocked !== true && this.activeShop) {
      clearServingStaff(this.activeShop.shopName);
      this._servingStaff = null;
    }
    this._dayBlocked = blocked;
  }

  // The staff bar owns PIN entry and persistence; the till only needs to know
  // whose id to stamp on the next sale.
  private _onServingChanged(e: CustomEvent<{ serving: IServingStaff | null }>) {
    this._servingStaff = e.detail.serving;
  }

  // True when this shop can record who took a sale at all. staff/verify-pin is
  // Silver+, so below that the bar is hidden rather than shown and refused.
  private get _staffPinsAllowed(): boolean {
    return this.activeShop?.subscription?.allowsMultiStaffPins === true;
  }

  // Submit the sale. With no argument it uses the quick single-method path
  // (`_paymentMethod` + any applied credit). The P3 split composer passes its
  // explicit, resolved tenders instead. `hasCredit` (split) or an applied credit
  // (quick) requires the stable idempotency key so a retry re-redeems the same
  // note rather than double-spending.
  private async _submit(split?: {
    tenders: ICreateSaleTender[];
    hasCredit: boolean;
  }) {
    if (this._busy || !this.activeShop || this._cart.length === 0) return;
    this._busy = true;
    this._error = null;
    try {
      const api = ApiFactory.authenticated();
      // B1 — stamp the device id so the server can pick the bound
      // CashDrawer for cash sales in multi-drawer mode. Single-drawer
      // shops ignore the id and route to "Main" either way.
      const { getOrCreateDeviceId } = await import('../datatypes/device');
      const deviceId = await getOrCreateDeviceId();

      let tenders: ICreateSaleTender[];
      let needsKey: boolean;
      if (split) {
        // P3 split: every tender already has an explicit amount.
        tenders = split.tenders;
        needsKey = split.hasCredit;
      } else {
        // Quick path: an applied credit note is the first tender (explicit
        // amount + note id); the chosen cash/card method settles the remainder
        // via an amount-less tender (server fills it) - unless credit already
        // covers the whole bill, in which case there's no cash/card tender.
        tenders = this._appliedCredit
          ? [
              {
                method: 'credit',
                amount: this._appliedCredit.amount,
                reference: this._appliedCredit.note.id,
              },
              ...(this._amountDue > 0 ? [{ method: this._paymentMethod }] : []),
            ]
          : [{ method: this._paymentMethod }];
        needsKey = this._appliedCredit !== null;
      }
      // A credit tender requires a stable idempotency key (server uses it as
      // Sale.Id) so a retry re-redeems the same note instead of double-spending.
      if (needsKey) this._creditIdempotencyKey ??= newSaleIdempotencyKey();
      const r = await api.createSale(this.activeShop.shopName, {
        tenders,
        lines: this._cart.map((l) => ({
          productId: l.product.id,
          cellId: l.cellId,
          quantity: l.quantity,
          selectedModifierGroups: l.selectedModifierGroups,
          discount: l.discount ?? null,
        })),
        cartDiscount: this._cartDiscount,
        deviceId,
        ...(needsKey && this._creditIdempotencyKey
          ? { idempotencyKey: this._creditIdempotencyKey }
          : {}),
        // Customer-loyalty pivot — attach the member (if one was scanned /
        // entered). The server re-resolves + validates; a stale code just
        // records an anonymous sale.
        memberCode: this._member?.memberCode ?? null,
        // N.7.4 — whose name goes on this sale. The server re-resolves it and
        // ignores an unknown or retired id, so a stale name records an
        // unattributed sale rather than failing the customer at the counter.
        actingStaffId: this._servingStaff?.loginId ?? null,
      });
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Sale failed.';
        return;
      }
      this._cart = [];
      this._cartDiscount = null;
      this._member = null;
      this._appliedCredit = null;
      this._creditIdempotencyKey = null;
      this._memberCreditNotes = [];
      this._lastSale = r.data;
      // Close the payment entry points (idempotent if already closed).
      this._cashDialogOpen = false;
      this._splitDialogOpen = false;
    } finally {
      this._busy = false;
    }
  }

  // Y.C1 — dismiss the customer-handoff confirmation and return to the
  // empty register. Cart is already cleared by _submit; this only
  // releases the full-screen overlay state.
  private _dismissConfirmation() {
    this._lastSale = null;
  }

  // The tender row. One question - how is this person paying - answered in one place, where
  // the button a cashier presses IS the commit.
  //
  // What this replaces, and why. The tender used to be picked in an sl-select and committed by
  // a separate "Take payment" button, with `_paymentMethod` persisting BETWEEN SALES. So a
  // cashier could carry "Card" over from the previous customer, press Take payment, and record
  // a card sale against cash that was physically in the drawer - with nothing on the button
  // they pressed naming the tender it would use. That discrepancy surfaces at close and lands
  // on a person. Collapsing the choice into the commit removes the state rather than warning
  // about it. Store credit and splitting were two further text buttons elsewhere, so paying was
  // asked in three places at three weights.
  //
  // Cash and card carry primary weight and their own amount because they are nearly every
  // transaction; the rest stay quiet beside them. The two are deliberately PEERS rather than a
  // strong and a weak primary: which one dominates varies by shop, so weighting one implies a
  // default that is not ours to set. The hierarchy that matters - commits now versus opens a
  // flow - is carried by the two rows.
  private _renderTenders() {
    const disabled = this._busy || this._cart.length === 0 || this._dayBlocked;

    // Credit already covers the bill, so there is nothing left to collect and offering tenders
    // would invite a second, unnecessary one. One action, naming what it is settling with.
    if (this._appliedCredit && this._amountDue <= 0) {
      return html`
        <div class="tender-row">
          <sl-button
            class="tender tender-primary"
            variant="primary"
            ?loading=${this._busy && !this._cashDialogOpen && !this._splitDialogOpen}
            ?disabled=${disabled}
            @click=${() => void this._submit()}
          >
            <sl-icon slot="prefix" name="wallet2"></sl-icon>
            Ring up with store credit
          </sl-button>
        </div>
      `;
    }

    const due = money(this._amountDue);
    return html`
      <div class="tender-row">
        <sl-button
          class="tender tender-primary"
          variant="primary"
          ?loading=${this._busy && !this._cashDialogOpen && !this._splitDialogOpen}
          ?disabled=${disabled}
          @click=${() => this._takeWith('cash')}
        >
          <sl-icon slot="prefix" name="cash-stack"></sl-icon>
          Cash ${due}
        </sl-button>
        <sl-button
          class="tender tender-primary"
          variant="primary"
          ?loading=${this._busy && !this._cashDialogOpen && !this._splitDialogOpen}
          ?disabled=${disabled}
          @click=${() => this._takeWith('card')}
        >
          <sl-icon slot="prefix" name="credit-card"></sl-icon>
          Card ${due}
        </sl-button>
      </div>
      <div class="tender-row tender-row-secondary">
        ${
          // Hidden once a note is applied: a sale settles at most one credit note, so offering
          // it again would promise something the sale cannot do.
          !this._appliedCredit
            ? html`<sl-button
                class="tender"
                size="small"
                ?disabled=${disabled || this._grandTotal <= 0}
                @click=${this._openCreditPicker}
              >
                <sl-icon slot="prefix" name="wallet2"></sl-icon>
                Store credit
              </sl-button>`
            : nothing
        }
        <sl-button
          class="tender"
          size="small"
          ?disabled=${disabled || this._grandTotal <= 0}
          @click=${this._openSplit}
        >
          <sl-icon slot="prefix" name="layout-split"></sl-icon>
          Split
        </sl-button>
        <!--
          "Other" is deliberately vague, and stays that way. It is the tender for payment of a
          scope this software does not model: GCash, barter, trade, a direct transfer, whatever a
          particular shop does that nobody enumerated. Naming it more precisely would be an
          improvement only if the list were complete, and the whole point is that it is not - the
          moment it becomes a list it stops covering the case it exists for. A shop that needs
          GCash reported on its own is asking for a modelled tender, which is a different and
          larger decision. Architect's ruling, 2026-08-29.
        -->
        <sl-button
          class="tender"
          size="small"
          ?disabled=${disabled}
          @click=${() => this._takeWith('other')}
        >
          <sl-icon slot="prefix" name="three-dots"></sl-icon>
          Other
        </sl-button>
      </div>
    `;
  }

  // Commit with one tender. The method is set at the moment of the tap rather than held as a
  // standing selection, which is the whole point of the row above: `_paymentMethod` can no
  // longer be stale, because nothing sets it until the cashier says how they are being paid.
  private _takeWith(method: 'cash' | 'card' | 'other') {
    if (this._busy || this._cart.length === 0) return;
    this._paymentMethod = method;
    this._onTakePayment();
  }

  // "Take payment" entry point. When store credit covers the whole bill there's
  // nothing left to collect, so ring up directly. Otherwise the remainder is
  // taken by the chosen method: cash routes via the cash-register dialog (so the
  // cashier can enter cash given + see change for the remaining due); card /
  // other go straight to _submit - there's nothing to compute.
  private _onTakePayment() {
    if (this._busy || this._cart.length === 0) return;
    // Clear any prior failure so a stale message doesn't greet the cashier
    // on the next attempt (the cash dialog surfaces _error while open).
    this._error = null;
    if (this._amountDue <= 0) {
      // Credit (or a free sale) covers everything - no cash/card to collect.
      void this._submit();
      return;
    }
    if (this._paymentMethod === 'cash') {
      this._cashDialogOpen = true;
      return;
    }
    void this._submit();
  }

  // Returns items that pass the current filters.
  //
  // Filter precedence: search wins. If the operator typed something,
  // we search across ALL categories (a category-scoped search is
  // confusing — "I typed 'fries' while Drinks was active and saw
  // nothing"). The active category becomes a no-op while search has
  // text; it re-asserts itself as soon as the search box is cleared.
  //
  // No search → category filter applies (or All / null = no filter).
  // Match is on name + id + resolved category name (so typing
  // "espresso" surfaces the Espresso category items too).
  private _filteredItems(): IProduct[] {
    const q = this._search.trim().toLowerCase();
    if (q) {
      return this._items.filter((item) => this._matchesQuery(item, q));
    }
    const cat = this._activeCategory;
    if (cat === null) return this._items;
    return this._items.filter((item) => {
      if (cat === '__uncategorised__') return !item.categoryId;
      return item.categoryId === cat;
    });
  }

  // Search match for the register. Covers name, id, resolved category
  // name, and any cell barcode — so a clerk can find an item by typing
  // part of a scanned code too. `q` is already lower-cased and trimmed.
  private _matchesQuery(item: IProduct, q: string): boolean {
    if (item.name.toLowerCase().includes(q)) return true;
    if (item.id.toLowerCase().includes(q)) return true;
    if (this._categoryName(item.categoryId).toLowerCase().includes(q))
      return true;
    for (const cell of item.cells ?? []) {
      for (const code of cell.barcodes ?? []) {
        if (code.toLowerCase().includes(q)) return true;
      }
    }
    return false;
  }

  // Exact barcode match for a scanned/typed code. Returns the product +
  // the cell the code is on. Case-insensitive, trimmed. Null when nothing
  // matches exactly. Resolved locally against the loaded menu (offline-ok).
  private _resolveCode(
    code: string
  ): { product: IProduct; cell: IVariantCell } | null {
    const c = code.trim().toLowerCase();
    if (!c) return null;
    for (const product of this._items) {
      for (const cell of product.cells ?? []) {
        for (const bc of cell.barcodes ?? []) {
          if (bc.toLowerCase() === c) return { product, cell };
        }
      }
    }
    return null;
  }

  // Enter in the search box = "ring up what I just scanned/typed". An
  // exact barcode match adds that cell (variant-resolved) and clears the
  // box for the next scan. Otherwise, if the filter narrows to exactly one
  // product, add it. Anything else leaves the text as a filter.
  private _onSearchEnter() {
    const text = this._search.trim();
    if (!text) return;
    // Scan classifier — the software (not the scanner) decides what a
    // scanned/typed code means. Ordered recognisers:
    //   1. member code  — a wallet QR payload (PAM:<code>) → attach member
    //   2. product barcode — exact cell-barcode match → ring up
    //   3. otherwise → leave the text as a search filter
    // (A future admin-control recogniser slots in ahead of the filter.)
    const memberCode = parseMemberQr(text);
    if (memberCode) {
      this._search = '';
      void this._attachMemberByCode(memberCode);
      return;
    }
    const hit = this._resolveCode(text);
    if (hit) {
      this._beginAdd(hit.product, hit.cell.id);
      this._search = '';
      return;
    }
    const filtered = this._filteredItems();
    if (filtered.length === 1) {
      this._beginAdd(filtered[0]);
      this._search = '';
    }
  }

  // Resolve a member code against THIS shop and attach the member to the
  // cart. Used by both the scan classifier (wallet QR) and the manual
  // "Add member" dialog. Surfaces a not-found message rather than throwing.
  private async _attachMemberByCode(code: string): Promise<boolean> {
    if (!this.activeShop || this._memberBusy) return false;
    this._memberBusy = true;
    this._memberLookupError = null;
    try {
      const api = ApiFactory.authenticated();
      const r = await api.resolveMemberCode(
        this.activeShop.shopName,
        code.trim()
      );
      if (!r.success || !r.data) {
        this._memberLookupError = `No member found for code "${code.trim().toUpperCase()}".`;
        return false;
      }
      this._member = {
        memberCode: r.data.memberCode,
        memberName: r.data.memberName,
        loginId: r.data.loginId,
      };
      this._memberDialogOpen = false;
      this._memberCodeInput = '';
      void this._loadMemberContext(r.data.loginId);
      return true;
    } finally {
      this._memberBusy = false;
    }
  }

  // Pull the member's welcome context (store credit + recent purchases) once
  // they're attached. Best-effort: a failure just hides the panel, never blocks
  // the sale.
  private async _loadMemberContext(loginId: string): Promise<void> {
    if (!this.activeShop) return;
    this._memberContext = null;
    this._memberContextLoading = true;
    try {
      const r = await ApiFactory.authenticated().getMemberTillContext(
        this.activeShop.shopName,
        loginId
      );
      // Guard against a race where the member was removed/changed mid-fetch.
      if (r.success && r.data && this._member?.loginId === loginId) {
        this._memberContext = r.data;
      }
    } finally {
      this._memberContextLoading = false;
    }
  }

  // Lazily load the attached member's redeemable notes (if a member is at the
  // till) so the credit picker can list them for one-tap apply. Best-effort: a
  // failed load just leaves an empty list; the code/QR path still works.
  private async _loadMemberCreditNotes(): Promise<void> {
    const loginId = this._member?.loginId;
    if (!loginId || !this.activeShop || this._creditNotesLoading) return;
    this._creditNotesLoading = true;
    try {
      const r = await ApiFactory.authenticated().listCreditNotesByMember(
        this.activeShop.shopName,
        loginId
      );
      // Guard a race where the member changed mid-fetch.
      if (r.success && r.data && this._member?.loginId === loginId) {
        this._memberCreditNotes = r.data.notes;
      }
    } finally {
      this._creditNotesLoading = false;
    }
  }

  // P2c - open the store-credit picker (quick path: apply one note + settle the
  // remainder with the chosen method).
  private async _openCreditPicker(): Promise<void> {
    this._error = null;
    this._creditDialogOpen = true;
    await this._loadMemberCreditNotes();
  }

  // P3 - open the split-payment composer (1 cash + 1 card + N credit). Preloads
  // the member's notes so credit lines can be added from the list too.
  private async _openSplit(): Promise<void> {
    if (this._cart.length === 0 || this._grandTotal <= 0) return;
    this._error = null;
    this._splitDialogOpen = true;
    await this._loadMemberCreditNotes();
  }

  // P3 - the composer resolved a full split; submit it.
  private _onSplitConfirmed(
    e: CustomEvent<{ tenders: ICreateSaleTender[]; hasCredit: boolean }>
  ): void {
    void this._submit(e.detail);
  }

  // The picker chose a note. Spend min(redeemable, grand total) of it on this
  // sale - never more than the bill (only cash gives change), never more than
  // the note holds. Stamp a stable idempotency key for the credit redemption.
  private _onCreditSelected(e: CustomEvent<{ note: ICreditNote }>): void {
    const note = e.detail.note;
    const spendable = creditNoteRedeemable(note);
    const amount = Math.min(spendable, this._grandTotal);
    if (amount <= 0) {
      this._error =
        'That note has no balance that can be applied to this sale.';
      this._creditDialogOpen = false;
      return;
    }
    this._appliedCredit = { note, amount };
    this._creditIdempotencyKey ??= newSaleIdempotencyKey();
    this._creditDialogOpen = false;
  }

  // Remove the applied store credit and discard its idempotency key (the next
  // application starts a fresh redemption attempt).
  private _removeCredit(): void {
    this._appliedCredit = null;
    this._creditIdempotencyKey = null;
  }

  // Keep applied credit consistent with the live cart: re-clamp to the new
  // grand total, and drop it entirely once the bill can no longer absorb any
  // credit (cart emptied or fully discounted). Called when the cart changes.
  private _reconcileCredit(): void {
    if (!this._appliedCredit) return;
    if (this._grandTotal <= 0) {
      this._removeCredit();
      return;
    }
    const spendable = creditNoteRedeemable(this._appliedCredit.note);
    const amount = Math.min(spendable, this._grandTotal);
    if (amount <= 0) {
      this._removeCredit();
      return;
    }
    if (amount !== this._appliedCredit.amount) {
      this._appliedCredit = { note: this._appliedCredit.note, amount };
    }
  }

  // Open the "Same again" picker for the member's most recent order. The
  // till-context line only carries product + cell + qty (NOT the modifier
  // selections), so a faithful re-order must route each line back through the
  // normal add dialog where size/modifiers are chosen — never a silent re-add.
  private _openReorder(): void {
    if (!this._memberContext?.recentPurchases?.[0]?.items?.length) return;
    this._reorderAdded = new Set();
    this._reorderActiveIndex = null;
    this._reorderOpen = true;
  }

  private _closeReorder(): void {
    this._reorderOpen = false;
    this._reorderActiveIndex = null;
  }

  // Route one line of the last order through the normal add flow: prefill the
  // option dialog with the historical cell + qty, then let the cashier confirm
  // size/modifiers before it lands. The reorder picker stays open underneath
  // (stacked) so they can work through the order line by line. An unavailable
  // product or a vanished cell just can't be added (the row is disabled).
  private _reorderAddItem(it: IMemberPurchaseItem, index: number): void {
    const product = this._items.find((p) => p.id === it.productId);
    if (!product || this.availability?.isUnavailable(product.id)) return;
    const hasVariants = this._hasVariants(product);
    const cell = (product.cells ?? []).find((c) => c.id === it.cellId) ?? null;
    // Resolve the starting cell: the historical one if it still exists, else
    // the single sensible default (simple product / single-cell product),
    // else null so the cashier picks (gated by the SELECT banner).
    let initialCellId = cell?.id ?? null;
    if (!initialCellId && !hasVariants)
      initialCellId = product.cells?.[0]?.id ?? null;
    else if (!initialCellId && this._activeCells(product).length === 1)
      initialCellId = this._activeCells(product)[0].id;
    this._reorderActiveIndex = index;
    this._pendingItem = product;
    this._pendingCellId = initialCellId;
    this._pendingSelections = {};
    this._pendingQuantity = Math.max(1, it.quantity);
    this._pendingDiscount = null;
    this._editingLineId = null;
    this._error = null;
  }

  // The "Same again" picker. Lists the member's last order; each row routes
  // through the normal add dialog (which stacks over this one). Rows for a
  // product that no longer exists or is currently unavailable are disabled;
  // already-added rows show a ✓.
  private _renderReorderDialog() {
    if (!this._reorderOpen) return nothing;
    const last = this._memberContext?.recentPurchases?.[0];
    const items = last?.items ?? [];
    const when = last?.createdUtc ? new Date(last.createdUtc) : null;
    return html`
      <sl-dialog
        class="full-page-on-phone"
        label="Same again"
        ?open=${this._reorderOpen}
        @sl-request-close=${this._closeReorder}
        @sl-after-hide=${this._closeReorder}
      >
        <div class="reorder-sub">
          ${when ? `From ${when.toLocaleDateString()} · ` : ''}Tap an item to
          add it — pick its size and options, then confirm.
        </div>
        <div class="reorder-list">
          ${items.map((it, i) => {
            const product = this._items.find((p) => p.id === it.productId);
            const unavailable =
              !product || !!this.availability?.isUnavailable(product?.id ?? '');
            const added = this._reorderAdded.has(i);
            const label = it.variantLabel
              ? `${it.name} · ${it.variantLabel}`
              : it.name;
            return html`
              <button
                class="reorder-row${added ? ' added' : ''}"
                ?disabled=${unavailable}
                @click=${() => this._reorderAddItem(it, i)}
              >
                <span class="reorder-qty">${it.quantity}×</span>
                <span class="reorder-name">${label}</span>
                ${
                  added
                    ? html`<sl-icon
                        class="reorder-tick"
                        name="check-lg"
                      ></sl-icon>`
                    : unavailable
                      ? html`<span class="reorder-na">Unavailable</span>`
                      : html`<sl-icon
                          class="reorder-add"
                          name="plus-lg"
                        ></sl-icon>`
                }
              </button>
            `;
          })}
        </div>
        <sl-button slot="footer" variant="primary" @click=${this._closeReorder}
          >Done</sl-button
        >
      </sl-dialog>
    `;
  }

  private _openMemberDialog() {
    this._memberCodeInput = '';
    this._memberLookupError = null;
    this._memberDialogOpen = true;
  }

  private _removeMember() {
    this._member = null;
    this._memberContext = null;
    this._memberContextLoading = false;
    this._closeReorder();
    this._reorderAdded = new Set();
    // Drop the member's cached credit-note list (the picker re-loads on open).
    // Any note already APPLIED stays applied - it's a chosen instrument now,
    // independent of who is attached, and the server validates it on redeem.
    this._memberCreditNotes = [];
  }

  private _renderMenuGrid() {
    const filtered = this._filteredItems();
    // Split into available (top, grouped by category) and currently-unavailable
    // (a single tray at the bottom). Cashier scans available items first; the
    // tray exists so an out-of-stock item is still visible / re-enableable.
    const available: IProduct[] = [];
    const unavailable: IProduct[] = [];
    for (const item of filtered) {
      if (this.availability?.isUnavailable(item.id)) unavailable.push(item);
      else available.push(item);
    }
    const groups = new Map<string | null, IProduct[]>();
    for (const item of available) {
      const key = item.categoryId;
      let bucket = groups.get(key);
      if (!bucket) {
        bucket = [];
        groups.set(key, bucket);
      }
      bucket.push(item);
    }
    const orderedKeys = [
      ...this._categories.map((c) => c.id as string | null),
      ...[...groups.keys()].filter(
        (k) => !this._categories.some((c) => c.id === k)
      ),
    ];

    const isFiltering = this._search.trim().length > 0;

    return html`
      ${this._renderStickyBar(filtered.length)}
      ${
        filtered.length === 0 && isFiltering
          ? html`
              <sl-card>
                <p style="margin: 0;">
                  No menu items match
                  <strong>"${this._search}"</strong>.
                </p>
              </sl-card>
            `
          : nothing
      }
      ${orderedKeys
        .filter((k) => groups.has(k))
        .map(
          (k) => html`
            <div class="group-title">${this._categoryName(k)}</div>
            <div class="menu-grid">
              ${(groups.get(k) ?? []).map((item) => this._renderMenuCard(item))}
            </div>
          `
        )}
      ${
        unavailable.length > 0
          ? html`
              <div class="unavailable-tray">
                <div class="group-title">
                  Currently unavailable (${unavailable.length})
                </div>
                <div class="menu-grid">
                  ${unavailable.map((item) => this._renderMenuCard(item))}
                </div>
              </div>
            `
          : nothing
      }
    `;
  }

  // Renders the sticky bar at the top of the menu column: search input
  // first, then the category-filter chip strip. Both stay pinned while
  // the cashier scrolls the menu grid.
  //
  // Chip behaviour follows two modes based on search state:
  //
  //   Search active   — chips reflect WHICH categories matched the
  //                     search (multi-highlight). The active-category
  //                     filter is paused — search wins. Clicking a
  //                     highlighted chip pins that category (clears
  //                     search + sets active). Chips for categories
  //                     with zero matches are hidden.
  //
  //   Search empty    — single-select. Clicking a chip filters; All
  //                     clears. Chips for empty categories are
  //                     hidden (the active one stays even if its
  //                     count is zero so the cashier sees their pick).
  private _renderStickyBar(matchCount: number) {
    const isFiltering = this._search.trim().length > 0;
    const total = this._items.length;
    // Pre-compute the set of category ids that have at least one
    // search match — these are the chips that highlight in search
    // mode. In search-empty mode, this still drives "hide chips for
    // empty categories."
    const q = this._search.trim().toLowerCase();
    const matchedCategoryIds = new Set<string>();
    let matchedUncategorised = false;
    for (const item of this._items) {
      const matches = isFiltering ? this._matchesQuery(item, q) : true;
      if (!matches) continue;
      if (item.categoryId) matchedCategoryIds.add(item.categoryId);
      else matchedUncategorised = true;
    }
    const chipPressed = (id: string | '__uncategorised__'): boolean => {
      if (isFiltering) {
        // Multi-highlight: any chip with a match is pressed.
        return id === '__uncategorised__'
          ? matchedUncategorised
          : matchedCategoryIds.has(id);
      }
      return this._activeCategory === id;
    };
    const onChipClick = (id: string | '__uncategorised__') => {
      // While searching: pin the category (clears search, sets active).
      // While not searching: toggle (active → null).
      if (isFiltering) {
        this._search = '';
        this._activeCategory = id;
      } else {
        this._activeCategory = this._activeCategory === id ? null : id;
      }
    };
    return html`
      <div class="menu-stickybar">
        <till-staff-bar
          .shopName=${this.activeShop?.shopName ?? ''}
          .enabled=${this._staffPinsAllowed}
          @serving-changed=${this._onServingChanged}
        ></till-staff-bar>
        ${this._renderMemberBar()}
        <sl-input
          placeholder="Search or scan (name, category, barcode)…"
          clearable
          autocomplete="off"
          .value=${this._search}
          @sl-input=${(e: Event) => {
            this._search = (e.target as HTMLInputElement).value;
          }}
          @sl-clear=${() => {
            this._search = '';
          }}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Escape') this._search = '';
            // Enter rings up the single filtered result when the search
            // narrows to exactly one product; otherwise it's a no-op.
            if (e.key === 'Enter') {
              e.preventDefault();
              this._onSearchEnter();
            }
          }}
        >
          <sl-icon slot="prefix" name="search"></sl-icon>
        </sl-input>

        <div class="cat-strip" role="tablist" aria-label="Filter by category">
          <button
            class="cat-chip"
            role="tab"
            aria-pressed=${!isFiltering && this._activeCategory === null ? 'true' : 'false'}
            @click=${() => {
              this._search = '';
              this._activeCategory = null;
            }}
          >
            All
          </button>
          ${this._categories.map((c) => {
            const hasMatches = matchedCategoryIds.has(c.id);
            // Hide chips that have neither a match nor are the active
            // single-select pick — keeps the strip tight.
            if (!hasMatches && this._activeCategory !== c.id) return nothing;
            return html`
              <button
                class="cat-chip"
                role="tab"
                aria-pressed=${chipPressed(c.id) ? 'true' : 'false'}
                @click=${() => onChipClick(c.id)}
              >
                ${c.name}
              </button>
            `;
          })}
          ${
            matchedUncategorised || this._activeCategory === '__uncategorised__'
              ? html`
                  <button
                    class="cat-chip"
                    role="tab"
                    aria-pressed=${chipPressed('__uncategorised__') ? 'true' : 'false'}
                    @click=${() => onChipClick('__uncategorised__')}
                  >
                    Uncategorised
                  </button>
                `
              : nothing
          }
        </div>

        ${
          isFiltering || this._activeCategory !== null
            ? html`
                <div class="menu-search-meta">
                  ${matchCount} of ${total}
                  ${matchCount === 1 ? 'item' : 'items'}
                  match${isFiltering ? ' · press Esc to clear search' : ''}
                </div>
              `
            : nothing
        }
      </div>
    `;
  }

  // The unified menu tile: image on top (or "No image" placeholder),
  // name + price below. Tap to add to cart, or — for a product with
  // variants and/or required modifiers — to open the picker. ⋮ on the
  // top-right opens the availability dialog without firing the buy
  // action. Sold-out items are desaturated and the buy action is
  // suppressed at click time.
  //
  // A dimensioned product shows "from ₱X" (the cheapest active cell),
  // since the exact price depends on the variant the cashier picks in
  // the dialog. A simple product shows its single default cell's price.
  private _renderMenuCard(item: IProduct) {
    const src = resolveAssetUrl(item.imageUrl);
    const unavailable = this.availability?.isUnavailable(item.id);
    const activeCells = this._activeCells(item);
    const thumb = src
      ? html`<img src=${src} alt=${item.name} loading="lazy" />`
      : html`<sl-icon name="box-seam"></sl-icon>`;
    const priceLabel =
      activeCells.length === 0
        ? ''
        : this._hasVariants(item)
          ? `from ${money(Math.min(...activeCells.map((c) => c.price)))}`
          : `${money(activeCells[0].price)}`;
    return html`
      <button
        class="menu-card${unavailable ? ' unavailable' : ''}"
        @click=${() => this._onProductClick(item)}
      >
        <div class="thumb">
          ${thumb}
          ${
            unavailable
              ? html`<div class="sold-out-overlay">Sold out</div>`
              : nothing
          }
        </div>
        <div class="meta">
          <strong>${item.name}</strong>
          <span class="price">${priceLabel}</span>
        </div>
        <sl-icon-button
          class="more-action"
          name="three-dots-vertical"
          label="Availability"
          @click=${(e: Event) => {
            e.stopPropagation();
            this._openAvailabilityDialog(item);
          }}
        ></sl-icon-button>
      </button>
    `;
  }

  private _renderOptionDialog() {
    if (!this._pendingItem) return nothing;
    const item = this._pendingItem;
    const activeCells = this._activeCells(item);
    const hasVariants = this._hasVariants(item);
    // The cell whose price drives the running total. For a dimensioned
    // product, the cashier's pick; for a simple product, the single
    // default cell.
    const activeCell = hasVariants
      ? (activeCells.find((c) => c.id === this._pendingCellId) ?? null)
      : (activeCells[0] ?? null);
    const selected: ISelectedModifierGroup[] = Object.entries(
      this._pendingSelections
    )
      .filter(([, entries]) => entries.length > 0)
      .map(([groupId, entries]) => ({ groupId, selections: entries }));
    const cellBase = activeCell?.price ?? 0;
    const runningTotal = activeCell
      ? this._computeUnitPrice(item, activeCell, selected)
      : 0;
    const totalDelta = runningTotal - cellBase;
    // Negative deltas use a minus sign rather than "(-X)" for readability.
    const formatDelta = (d: number) =>
      d === 0 ? '' : ` (${d > 0 ? '+' : '−'}${money(Math.abs(d))})`;
    const lineTotal = runningTotal * Math.max(1, this._pendingQuantity || 1);
    // Required choices still outstanding — drives the SELECT banner and the
    // Add gate. While non-empty the line cannot be confirmed.
    const unmet = this._unmetRequired();
    return html`
      <sl-dialog
        class="full-page-on-phone"
        open
        label="${this._editingLineId ? `Edit ${item.name}` : item.name}"
        @sl-after-hide=${this._cancelOptions}
      >
        ${
          unmet.length
            ? html`<div class="required-banner" role="alert">
                Select ${unmet.join(' & ')}
              </div>`
            : nothing
        }
        <div class="option-total">
          <span class="option-total-base">
            Base
            ${money(cellBase)}${
              totalDelta !== 0
                ? html` <span class="option-total-delta"
                    >${totalDelta > 0 ? '+' : '−'}${money(Math.abs(totalDelta))}</span
                  >`
                : nothing
            }
          </span>
          <span class="option-total-amount">${money(lineTotal)}</span>
        </div>

        ${
          hasVariants
            ? html`
                <div style="margin: 8px 0;">
                  <strong>${this._variantChoiceLabel(item)} *</strong>
                  <div
                    style="display: flex; flex-wrap: wrap; gap: 8px; margin-top: 6px;"
                  >
                    ${activeCells.map((c) => {
                      const isSelected = c.id === this._pendingCellId;
                      const label = this._cellLabel(item, c) ?? c.id;
                      return html`
                        <sl-button
                          size="small"
                          variant=${isSelected ? 'primary' : 'default'}
                          @click=${() => {
                            this._pendingCellId = c.id;
                          }}
                        >
                          ${label} · ${money(c.price)}
                        </sl-button>
                      `;
                    })}
                  </div>
                </div>
              `
            : nothing
        }

        <div class="dialog-qty-row">
          <strong>Quantity</strong>
          <div class="dialog-qty-stepper">
            <sl-icon-button
              name="dash-circle"
              label="Decrease"
              ?disabled=${this._pendingQuantity <= 1}
              @click=${() => this._bumpPendingQuantity(-1)}
            ></sl-icon-button>
            <span class="dialog-qty-value">${this._pendingQuantity}</span>
            <sl-icon-button
              name="plus-circle"
              label="Increase"
              @click=${() => this._bumpPendingQuantity(1)}
            ></sl-icon-button>
          </div>
          ${
            this._pendingQuantity > 1
              ? html`<small class="dialog-qty-each">
                  @ ${money(runningTotal)} each
                </small>`
              : nothing
          }
        </div>
        ${item.modifierGroups.map(
          (group) => html`
            <div style="margin: 8px 0;">
              <strong>${group.name}${group.required ? ' *' : ''}</strong>
              <div
                style="display: flex; flex-wrap: wrap; gap: 8px; margin-top: 6px;"
              >
                ${group.modifiers.map((opt) => {
                  const sel = this._pendingSelections[group.id] ?? [];
                  const picked = sel.find((e) => e.modifierId === opt.id);
                  const isSelected = picked !== undefined;
                  const qty = picked?.quantity ?? 0;
                  if (!group.multiSelect) {
                    return html`
                      <sl-button
                        size="small"
                        variant=${isSelected ? 'primary' : 'default'}
                        @click=${() => this._setOption(group, opt, true)}
                      >
                        ${opt.name}${formatDelta(opt.priceDelta)}
                      </sl-button>
                    `;
                  }
                  // Quantifiable option in a multi-select group — render an
                  // inline qty stepper alongside the toggle, so "Extra shot"
                  // can become "Extra shot ×2" without a separate dialog.
                  if (opt.quantifiable) {
                    const atCap =
                      opt.maxQuantity != null && qty >= opt.maxQuantity;
                    return html`
                      <div
                        class="opt-qty-row"
                        data-selected=${isSelected ? 'true' : 'false'}
                      >
                        <sl-checkbox
                          ?checked=${isSelected}
                          @sl-change=${(e: Event) =>
                            this._setOption(
                              group,
                              opt,
                              (e.target as HTMLInputElement).checked
                            )}
                        >
                          ${opt.name}${formatDelta(opt.priceDelta)}
                        </sl-checkbox>
                        ${
                          isSelected
                            ? html`
                                <div class="opt-qty-stepper">
                                  <sl-icon-button
                                    name="dash-circle"
                                    label="Decrease ${opt.name}"
                                    @click=${() => this._bumpOptionQuantity(group, opt, -1)}
                                  ></sl-icon-button>
                                  <span class="opt-qty-value">×${qty}</span>
                                  <sl-icon-button
                                    name="plus-circle"
                                    label="Increase ${opt.name}"
                                    ?disabled=${atCap}
                                    @click=${() => this._bumpOptionQuantity(group, opt, 1)}
                                  ></sl-icon-button>
                                  ${
                                    opt.maxQuantity != null
                                      ? html`<small class="opt-qty-cap"
                                          >max ${opt.maxQuantity}</small
                                        >`
                                      : nothing
                                  }
                                </div>
                              `
                            : nothing
                        }
                      </div>
                    `;
                  }
                  return html`
                    <sl-checkbox
                      ?checked=${isSelected}
                      @sl-change=${(e: Event) =>
                        this._setOption(
                          group,
                          opt,
                          (e.target as HTMLInputElement).checked
                        )}
                    >
                      ${opt.name}${formatDelta(opt.priceDelta)}
                    </sl-checkbox>
                  `;
                })}
              </div>
            </div>
          `
        )}
        ${this._renderDiscountBlock(
          'Line discount',
          this._pendingDiscount,
          (d) => {
            this._pendingDiscount = d;
          },
          lineTotal
        )}
        ${
          this._error
            ? html`<sl-alert variant="danger" open>${this._error}</sl-alert>`
            : nothing
        }
        ${
          this._editingLineId
            ? html`<sl-button
                slot="footer"
                variant="danger"
                @click=${this._removeLineFromDialog}
              >
                <sl-icon slot="prefix" name="trash"></sl-icon>
                Remove
              </sl-button>`
            : nothing
        }
        <sl-button slot="footer" @click=${this._cancelOptions}
          >Cancel</sl-button
        >
        <sl-button
          slot="footer"
          variant="primary"
          ?disabled=${unmet.length > 0}
          @click=${this._confirmOptions}
        >
          ${
            unmet.length
              ? `Select ${unmet.join(' & ')}`
              : `${this._editingLineId ? 'Update' : 'Add'} ${money(lineTotal - this._previewDiscount(lineTotal, this._pendingDiscount))}`
          }
        </sl-button>
      </sl-dialog>
    `;
  }

  // Shared discount editor: a tiny "None / Percent / Fixed" pill toggle + a
  // value input + an optional reason. Used for both line-level discounts
  // (inside the option dialog) and cart-level (in its own dialog).
  private _renderDiscountBlock(
    label: string,
    value: ISaleDiscount | null,
    onChange: (d: ISaleDiscount | null) => void,
    target: number
  ) {
    const kind: 'none' | 'percent' | 'fixed' = value?.kind ?? 'none';
    const amount = this._previewDiscount(target, value);
    const setKind = (k: 'none' | 'percent' | 'fixed') => {
      if (k === 'none') {
        onChange(null);
        return;
      }
      onChange({
        kind: k,
        value: value?.value ?? 0,
        reason: value?.reason ?? null,
      });
      this.requestUpdate();
    };
    const setValue = (v: number) => {
      if (!value) {
        onChange({ kind: 'percent', value: v, reason: null });
      } else {
        onChange({ ...value, value: v });
      }
      this.requestUpdate();
    };
    const setReason = (r: string) => {
      if (!value) return;
      onChange({ ...value, reason: r || null });
      this.requestUpdate();
    };
    return html`
      <div
        style="margin: 14px 0 6px 0; padding-top: 10px; border-top: 1px solid var(--sl-color-neutral-200);"
      >
        <div
          style="display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap;"
        >
          <strong style="font-size: 0.92rem;">${label}</strong>
          ${amount > 0 ? html`<span style="font-weight: 600;">−${money(amount)}</span>` : nothing}
        </div>
        <div style="display: flex; gap: 6px; flex-wrap: wrap; margin-top: 8px;">
          <sl-button
            size="small"
            variant=${kind === 'none' ? 'primary' : 'default'}
            @click=${() => setKind('none')}
            >None</sl-button
          >
          <sl-button
            size="small"
            variant=${kind === 'percent' ? 'primary' : 'default'}
            @click=${() => setKind('percent')}
            >Percent</sl-button
          >
          <sl-button
            size="small"
            variant=${kind === 'fixed' ? 'primary' : 'default'}
            @click=${() => setKind('fixed')}
            >Fixed</sl-button
          >
        </div>
        ${
          kind !== 'none'
            ? html`
                <div
                  style="display: grid; grid-template-columns: 1fr 1.4fr; gap: 8px; margin-top: 8px;"
                >
                  <sl-input
                    type="number"
                    min="0"
                    step="any"
                    placeholder=${kind === 'percent' ? '%' : activeCurrency().symbol}
                    .value=${value?.value != null ? String(value.value) : ''}
                    @sl-input=${(e: Event) => setValue(parseFloat((e.target as HTMLInputElement).value) || 0)}
                  ></sl-input>
                  <sl-input
                    placeholder="Reason (optional)"
                    .value=${value?.reason ?? ''}
                    @sl-input=${(e: Event) => setReason((e.target as HTMLInputElement).value)}
                  ></sl-input>
                </div>
              `
            : nothing
        }
      </div>
    `;
  }

  private _renderAvailabilityDialog() {
    const item = this._availabilityItem;
    if (!item) return nothing;
    const current = this.availability?.getUnavailable(item.id);
    const isCurrentlyUnavailable = !!current;
    return html`
      <sl-dialog
        class="full-page-on-phone"
        open
        label="${item.name} — availability"
        @sl-after-hide=${this._closeAvailabilityDialog}
      >
        ${
          isCurrentlyUnavailable
            ? html`
                <p class="avail-current">
                  Currently <strong>sold out</strong>
                  ${
                    current.reason === 'custom' && current.reasonNote
                      ? html` — <em>${current.reasonNote}</em>`
                      : html` — ${this._reasonLabel(current.reason)}`
                  }
                  ${
                    current.unavailableUntilUtc
                      ? html`<br /><small
                            >Until
                            ${new Date(current.unavailableUntilUtc).toLocaleString()}</small
                          >`
                      : html`<br /><small>Until manually re-enabled</small>`
                  }
                </p>
                <sl-divider></sl-divider>
                <p>Update the reason / until below, or mark available.</p>
              `
            : nothing
        }

        <div class="avail-row">
          <strong>Reason</strong>
          <sl-radio-group
            .value=${this._availReason}
            @sl-change=${(e: Event) => {
              this._availReason = (e.target as HTMLInputElement)
                .value as ProductAvailabilityReason;
            }}
          >
            <sl-radio value="out-of-stock">Out of stock</sl-radio>
            <sl-radio value="equipment-down">Equipment down</sl-radio>
            <sl-radio value="service-window">Service window</sl-radio>
            <sl-radio value="custom">Custom</sl-radio>
          </sl-radio-group>
        </div>

        ${
          this._availReason === 'custom'
            ? html`
                <sl-textarea
                  label="Note"
                  rows="2"
                  placeholder="Why is this item unavailable?"
                  .value=${this._availNote}
                  @sl-input=${(e: Event) => {
                    this._availNote = (e.target as HTMLTextAreaElement).value;
                  }}
                ></sl-textarea>
              `
            : html`
                <sl-input
                  label="Note (optional)"
                  placeholder="Optional context"
                  .value=${this._availNote}
                  @sl-input=${(e: Event) => {
                    this._availNote = (e.target as HTMLInputElement).value;
                  }}
                ></sl-input>
              `
        }

        <div class="avail-row">
          <strong>Until</strong>
          <sl-radio-group
            .value=${this._availUntilPreset}
            @sl-change=${(e: Event) => {
              this._availUntilPreset = (e.target as HTMLInputElement)
                .value as UntilPreset;
            }}
          >
            <sl-radio value="indefinite">Indefinite</sl-radio>
            <sl-radio value="1h">1 h</sl-radio>
            <sl-radio value="2h">2 h</sl-radio>
            <sl-radio value="4h">4 h</sl-radio>
            <sl-radio value="8h">8 h</sl-radio>
            <sl-radio value="custom">Custom…</sl-radio>
          </sl-radio-group>
        </div>

        ${
          this._availUntilPreset === 'custom'
            ? html`
                <sl-input
                  type="datetime-local"
                  label="Available again at"
                  .value=${this._availCustomUntil}
                  @sl-input=${(e: Event) => {
                    this._availCustomUntil = (
                      e.target as HTMLInputElement
                    ).value;
                  }}
                ></sl-input>
              `
            : nothing
        }
        ${
          this._availError
            ? html`<sl-alert variant="danger" open
                >${this._availError}</sl-alert
              >`
            : nothing
        }

        <sl-button slot="footer" @click=${this._closeAvailabilityDialog}>
          Cancel
        </sl-button>
        ${
          isCurrentlyUnavailable
            ? html`
                <sl-button
                  slot="footer"
                  variant="success"
                  ?loading=${this._availBusy}
                  @click=${this._confirmMarkAvailable}
                >
                  Mark available
                </sl-button>
                <sl-button
                  slot="footer"
                  variant="primary"
                  ?loading=${this._availBusy}
                  @click=${this._confirmMarkUnavailable}
                >
                  Update
                </sl-button>
              `
            : html`
                <sl-button
                  slot="footer"
                  variant="primary"
                  ?loading=${this._availBusy}
                  @click=${this._confirmMarkUnavailable}
                >
                  Mark unavailable
                </sl-button>
              `
        }
      </sl-dialog>
    `;
  }

  private _reasonLabel(reason: ProductAvailabilityReason): string {
    switch (reason) {
      case 'out-of-stock':
        return 'Out of stock';
      case 'equipment-down':
        return 'Equipment down';
      case 'service-window':
        return 'Service window';
      case 'custom':
        return 'Custom';
    }
  }

  // Y.C1 — customer-shareable confirmation page rendered immediately
  // after a successful sale. The cashier turns the device toward the
  // customer to scan the QR or note the URL; the "Thank you" button
  // releases the screen back to an empty register for the next order.
  // Designed deliberately spare — only the order number, the QR, the
  // URL, and the dismissal button — so the customer's eye lands on
  // what they need without scanning operator chrome.
  private _renderConfirmation(sale: ISale) {
    const url = `${window.location.origin}/track/${sale.trackToken}`;
    return html`
      <div class="confirmation-overlay">
        <div class="confirmation-body">
          <div class="confirmation-shop">
            ${this.activeShop?.shopDisplayName ?? this.activeShop?.shopName ?? ''}
          </div>
          ${
            sale.daySequence > 0
              ? html`
                  <div class="confirmation-order-number">
                    <span class="hash">#</span>${sale.daySequence}
                  </div>
                `
              : nothing
          }
          <div class="confirmation-headline">Thanks for your order</div>
          <div class="confirmation-sub">
            Scan the code to track your order live, or open the link below.
          </div>

          ${
            sale.trackToken
              ? html`
                  <order-qr
                    .token=${sale.trackToken}
                    .daySequence=${0}
                    size=${280}
                    .caption=${''}
                  ></order-qr>
                  <a
                    class="confirmation-url"
                    href="/track/${sale.trackToken}"
                    target="_blank"
                    rel="noopener"
                    >${url}</a
                  >
                `
              : html`
                  <div class="confirmation-sub">
                    (Tracking is not available for this order.)
                  </div>
                `
          }

          <sl-button
            class="confirmation-thank-you"
            variant="primary"
            size="large"
            @click=${this._dismissConfirmation}
          >
            Thank you
          </sl-button>
        </div>
      </div>
    `;
  }

  render() {
    if (this._loading) return html`<sl-card><p>Loading…</p></sl-card>`;
    if (!this.activeShop)
      return html`<sl-alert variant="warning" open
        >Pick a shop first.</sl-alert
      >`;

    // Y.C1 — customer-handoff confirmation overlay. After a successful
    // sale, the register transitions to a full-screen panel intended
    // to be turned toward the customer: large QR + track URL + the
    // order number. "Thank you" releases back to the empty cart. Cart
    // is already cleared in _submit, so no state ceremony on dismiss.
    if (this._lastSale) {
      return this._renderConfirmation(this._lastSale);
    }

    // Phase D — no open trading day: the till is gated. Show the day control so
    // the cashier can open the day right here, then the till reappears.
    if (this._dayBlocked) {
      return html`
        <div class="day-gate">
          <sl-icon name="shop"></sl-icon>
          <h2>No open trading day</h2>
          <p>Open the day before ringing up sales.</p>
          <shop-day-bar
            .shopName=${this.activeShop.shopName}
            @day-changed=${this._onDayChanged}
          ></shop-day-bar>
        </div>
      `;
    }

    // Phone / small-tablet viewport: show one pane at a time, switcher
    // at the top. Tablet+ desktop: both panes side-by-side as before.
    // `small=phone` means "show Menu only when _phoneTab=='menu', show
    // Order only when _phoneTab=='order'." Computed booleans drive a
    // [hidden] attribute on each pane — both panes stay in the DOM so
    // their state (cart contents, filter selection) survives toggle
    // round-trips.
    const showMenu = !this._smallViewport || this._phoneTab === 'menu';
    const showOrder = !this._smallViewport || this._phoneTab === 'order';

    return html`
      ${
        this._smallViewport
          ? html`
              <div class="phone-switch" role="tablist" aria-label="Switch pane">
                <button
                  role="tab"
                  aria-pressed=${this._phoneTab === 'menu' ? 'true' : 'false'}
                  @click=${() => (this._phoneTab = 'menu')}
                >
                  <sl-icon name="grid-3x3-gap"></sl-icon>
                  Menu
                </button>
                <button
                  role="tab"
                  aria-pressed=${this._phoneTab === 'order' ? 'true' : 'false'}
                  @click=${() => (this._phoneTab = 'order')}
                >
                  <sl-icon name="bag"></sl-icon>
                  Order
                  ${
                    this._cart.length > 0
                      ? html`<span class="badge">${this._cart.length}</span>`
                      : nothing
                  }
                </button>
              </div>
            `
          : nothing
      }

      <div class="layout">
        <div class="pane menu-pane" ?hidden=${!showMenu}>
          <div class="pane-scroll">
            <!-- Page title lives inside the scroll container so it
                 scrolls away naturally and hands off to the sticky
                 search/chips bar as you scroll the grid. Keeping it
                 outside left a non-scrolling label competing visually
                 with the actually-useful sticky bar. -->
            <h2 class="pane-title">Register</h2>
            ${
              this._items.length === 0
                ? html`
                    <sl-card>
                      <p>No active menu items yet.</p>
                      <sl-button
                        @click=${() => router.navigate(resolveRouterPath('products'))}
                      >
                        Go to Menu
                      </sl-button>
                    </sl-card>
                  `
                : this._renderMenuGrid()
            }
            ${
              this._smallViewport &&
              this._cart.length > 0 &&
              this._phoneTab === 'menu'
                ? html`
                    <div class="cart-summary-bar">
                      <sl-icon name="bag"></sl-icon>
                      <span class="count"
                        >${this._cart.length}
                        ${this._cart.length === 1 ? 'item' : 'items'}</span
                      >
                      <span class="total">${this._grandTotal.toFixed(2)}</span>
                      <sl-button
                        size="small"
                        variant="primary"
                        @click=${() => (this._phoneTab = 'order')}
                      >
                        Review order
                      </sl-button>
                    </div>
                  `
                : nothing
            }
          </div>
        </div>

        <div class="pane order-pane" ?hidden=${!showOrder}>
          <h3 style="margin: 0 0 8px 0;">Cart (${this._cart.length})</h3>
          ${
            this._cart.length === 0
              ? html`<div class="cart-empty">
                  <small>Tap items on the left to start a sale.</small>
                </div>`
              : this._renderCartReceipt()
          }

          <div class="order-footer">
            ${(() => {
              const lineDisc = this._lineDiscountTotal;
              const cartDisc = this._cartDiscountAmount;
              const hasDisc = lineDisc > 0 || cartDisc > 0;
              return html`
                ${
                  hasDisc
                    ? html`
                        <div class="total-row" style="opacity: 0.7;">
                          <span>Subtotal</span>
                          <span>${this._subtotal.toFixed(2)}</span>
                        </div>
                        ${
                          lineDisc > 0
                            ? html`
                                <div class="total-row" style="opacity: 0.7;">
                                  <span>Line discounts</span>
                                  <span>−${lineDisc.toFixed(2)}</span>
                                </div>
                              `
                            : nothing
                        }
                        ${
                          cartDisc > 0
                            ? html`
                                <div class="total-row" style="opacity: 0.7;">
                                  <span>Cart discount</span>
                                  <span>−${cartDisc.toFixed(2)}</span>
                                </div>
                              `
                            : nothing
                        }
                      `
                    : nothing
                }
                <div class="total-row">
                  <span>Total</span>
                  <span>${this._grandTotal.toFixed(2)}</span>
                </div>
              `;
            })()}

            <div style="margin-top: 6px;">
              <sl-button
                size="small"
                variant="text"
                ?disabled=${this._cart.length === 0}
                @click=${() => {
                  this._pendingCartDiscount = this._cartDiscount;
                  this._cartDiscountDialogOpen = true;
                }}
              >
                <sl-icon slot="prefix" name="percent"></sl-icon>
                ${this._cartDiscount ? 'Edit cart discount' : 'Discount cart'}
              </sl-button>
            </div>

            <div class="submit-block">
              ${
                this._appliedCredit
                  ? html`
                      <div
                        style="display:flex;align-items:center;justify-content:space-between;gap:8px;padding:8px 10px;margin-bottom:8px;border:1px solid var(--sl-color-success-300);background:var(--sl-color-success-50);border-radius:var(--sl-border-radius-medium);"
                      >
                        <span
                          style="display:flex;align-items:center;gap:6px;font-size:0.85rem;"
                        >
                          <sl-icon name="wallet2"></sl-icon>
                          <span
                            >Store credit
                            <strong
                              >−${money(this._appliedCredit.amount)}</strong
                            >
                            · ${this._appliedCredit.note.code}</span
                          >
                        </span>
                        <sl-button
                          size="small"
                          variant="text"
                          @click=${this._removeCredit}
                        >
                          Remove
                        </sl-button>
                      </div>
                      ${
                        this._amountDue > 0
                          ? html`<div
                              style="display:flex;justify-content:space-between;font-weight:600;margin-bottom:8px;"
                            >
                              <span>Remaining due</span
                              ><span>${money(this._amountDue)}</span>
                            </div>`
                          : nothing
                      }
                    `
                  : nothing
              }
              ${this._renderTenders()}
            </div>

            ${this._error ? html`<sl-alert variant="danger" open>${this._error}</sl-alert>` : nothing}

            <div class="actions">
              <sl-button
                variant="text"
                @click=${this._clearCart}
                ?disabled=${this._cart.length === 0}
              >
                Clear
              </sl-button>
            </div>
          </div>
        </div>
      </div>

      ${this._renderMemberDialog()} ${this._renderReorderDialog()}
      ${this._renderOptionDialog()} ${this._renderAvailabilityDialog()}
      ${this._renderCartDiscountDialog()}
      <cash-register-dialog
        .total=${this._amountDue}
        ?open=${this._cashDialogOpen}
        ?busy=${this._busy}
        .error=${this._cashDialogOpen ? this._error : null}
        @cash-confirmed=${() => this._submit()}
        @cash-cancelled=${() => {
          this._cashDialogOpen = false;
          this._error = null;
        }}
      ></cash-register-dialog>
      <credit-note-picker
        .amountDue=${this._grandTotal}
        .shopName=${this.activeShop?.shopName ?? ''}
        .memberNotes=${this._memberCreditNotes}
        .memberName=${this._member?.memberName ?? null}
        ?open=${this._creditDialogOpen}
        @credit-selected=${this._onCreditSelected}
        @credit-cancelled=${() => {
          this._creditDialogOpen = false;
          this._error = null;
        }}
      ></credit-note-picker>
      <split-payment-dialog
        .total=${this._grandTotal}
        .shopName=${this.activeShop?.shopName ?? ''}
        .memberNotes=${this._memberCreditNotes}
        .memberName=${this._member?.memberName ?? null}
        ?open=${this._splitDialogOpen}
        ?busy=${this._busy}
        .error=${this._splitDialogOpen ? this._error : null}
        @split-confirmed=${this._onSplitConfirmed}
        @split-cancelled=${() => {
          this._splitDialogOpen = false;
          this._error = null;
        }}
      ></split-payment-dialog>
    `;
  }

  private _renderCartDiscountDialog() {
    if (!this._cartDiscountDialogOpen) return nothing;
    const target = this._subtotal - this._lineDiscountTotal;
    return html`
      <sl-dialog
        class="full-page-on-phone"
        open
        label="Cart discount"
        @sl-after-hide=${() => {
          this._cartDiscountDialogOpen = false;
        }}
      >
        <div
          style="font-size: 0.88rem; color: var(--sl-color-neutral-600); margin-bottom: 8px;"
        >
          Applies to the order subtotal after any per-line discounts
          (${money(target)}).
        </div>
        ${this._renderDiscountBlock(
          'Cart discount',
          this._pendingCartDiscount,
          (d) => {
            this._pendingCartDiscount = d;
          },
          target
        )}
        <sl-button
          slot="footer"
          @click=${() => {
            this._cartDiscountDialogOpen = false;
          }}
          >Cancel</sl-button
        >
        <sl-button
          slot="footer"
          variant="primary"
          @click=${() => {
            this._cartDiscount = this._pendingCartDiscount;
            this._cartDiscountDialogOpen = false;
          }}
          >Apply</sl-button
        >
      </sl-dialog>
    `;
  }
}
